import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Server,
  Cpu,
  Terminal,
  Layers,
  CheckCircle2,
  Shield,
  ShieldCheck,
  Zap,
  ArrowRight,
  Sparkles,
  Lock,
  ChevronRight,
  HardDrive,
  HelpCircle,
  Clock,
  ChevronDown,
  Globe,
  Globe2,
  RefreshCw,
  Users,
  Check,
  X,
  AlertCircle,
  Database,
  Cloud,
  Mail,
  Sliders,
  PhoneCall,
  Search,
  ShoppingCart,
  CheckCheck,
  User,
  CreditCard,
  Building2,
  ArrowUpRight,
  LockKeyhole,
  FileCode,
  LifeBuoy,
  BadgePercent,
  Activity,
  Boxes,
  Key,
  Copy,
  Smartphone,
  Bell,
  Wifi,
  BatteryCharging,
  Download,
} from "lucide-react";
import api from "../api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CinematicServerSlider from "../components/CinematicServerSlider";
import CustomerAuthModal from "../components/CustomerAuthModal";
import MeoHostVdsView from "../components/MeoHostVdsView";
import { useAuth } from "../contexts/AuthContext";
import { useModals } from "../contexts/ModalContext";

export const isAnnualOnlyProduct = (p) => {
  if (!p) return false;
  const cat = (p.category || "").toLowerCase();
  const name = (p.name || "").toLowerCase();
  const slug = (p.slug || "").toLowerCase();
  return (
    cat === "domains" ||
    cat === "domain" ||
    cat === "ssl" ||
    name.includes("domain") ||
    slug.includes("domain") ||
    name.includes("ssl")
  );
};

// ============================================================================
// 1. DEFAULT PRODUCT DEFINITIONS BY CATEGORY (7 Modern Pillars)
// ============================================================================
const HOSTING_CATEGORIES = [
  {
    id: "webhosting",
    label: "Webhosting (NVMe)",
    badge: "NVMe Gen4",
    price: "Ab CHF 9.90 / Mt.",
    icon: Globe,
    desc: "Ultra-schneller NVMe SSD Webspace mit LiteSpeed Enterprise & cPanel",
  },
  {
    id: "wordpress",
    label: "WordPress Hosting",
    badge: "LSCache Boost",
    price: "Ab CHF 6.90 / Mt.",
    icon: Zap,
    desc: "Mit LiteSpeed Cache & AccelerateWP optimierte WordPress-Power",
  },
  {
    id: "vps",
    label: "Cloud VPS & VDS",
    badge: "KVM Root",
    price: "Ab CHF 19.90 / Mt.",
    icon: Server,
    desc: "Dedizierte KVM-Kerne, NVMe RAID-10 und 10 Gbit/s Schweizer Uplink",
  },
  {
    id: "reseller",
    label: "Reseller Hosting",
    badge: "100% White-Label",
    price: "Ab CHF 79.90 / Mt.",
    icon: Users,
    isFeatured: true,
    desc: "100% White-Label mit WHM, cPanel und eigener Kundenverwaltung",
  },
  {
    id: "mail",
    label: "E-Mail Hosting",
    badge: "SpamProtection",
    price: "Ab CHF 4.90 / Mt.",
    icon: Mail,
    desc: "Professionelle Firmen-Postfächer mit eigener Domain & SpamProtection",
  },
  {
    id: "ssl",
    label: "SSL Zertifikate",
    badge: "256-Bit SSL",
    price: "Inklusive / CHF 25",
    icon: Lock,
    desc: "Banking-Grade 256-Bit Verschlüsselung & offizielle Vertrauenssiegel",
  },
  {
    id: "domains",
    label: "Domain-Preise",
    badge: ".ch ab CHF 15.-",
    price: "Echtzeit-Prüfung",
    icon: Globe2,
    desc: "Übersicht aller Schweizer & internationaler Top-Level-Domains",
  },
];

const CATEGORY_PRODUCTS = {
  webhosting: [
    {
      id: "webhosting-basic",
      slug: "linux-nvme-webspace-basic",
      name: "Linux NVMe Webspace",
      displayName: "NVMe Webspace Basic",
      unitPrice: 9.90,
      badge: "EINSTEIGER",
      shortDescription: "Der ideale Einstieg mit rasend schnellem NVMe-Speicher für persönliche Websites, Blogs und kleine Firmen.",
      specs: { disk: "20 GB NVMe SSD", sites: "1 Website", ram: "1 GB RAM", cpu: "1 vCPU", mail: "10 Postfächer", traffic: "Unlimitiert" },
      features: [
        "20 GB Samsung NVMe PCIe 4.0 SSD",
        "1 Domain inklusive verwaltbar",
        "Unlimitierter High-Speed Traffic",
        "10 E-Mail Postfächer (je 5 GB)",
        "2 MariaDB 10.6 Datenbanken",
        "Kostenloses Let's Encrypt Auto-SSL",
        "Tägliche automatisierte Backups (7 Tage)",
        "PHP 7.4, 8.1, 8.2 & 8.3 Switcher",
        "cPanel & Softaculous (400+ 1-Klick Apps)",
        "100% Schweizer Rechenzentrum Zürich",
      ],
    },
    {
      id: "webhosting-professional",
      slug: "business-hosting-professional",
      name: "Business Hosting",
      displayName: "Business NVMe Pro",
      unitPrice: 19.90,
      badge: "BESTSELLER / EMPFOHLEN",
      isHighlight: true,
      shortDescription: "Maximale Geschwindigkeit und erweiterte Systemressourcen für professionelle Firmen-Websites.",
      specs: { disk: "50 GB NVMe SSD", sites: "5 Websites", ram: "2 GB RAM", cpu: "2 vCPU", mail: "50 Postfächer", traffic: "Unlimitiert" },
      features: [
        "50 GB Samsung NVMe PCIe 4.0 SSD",
        "5 Domains / Websites verwaltbar",
        "Unlimitierter Traffic mit 40 Gbit/s Backbone",
        "50 E-Mail Postfächer (je 10 GB)",
        "10 MariaDB Datenbanken mit phpMyAdmin",
        "LiteSpeed Enterprise mit LSCache & OPcache",
        "Kostenloses Auto-SSL für alle Domains",
        "Tägliche automatisierte Backups (14 Tage)",
        "SSH-Terminal, Git & WP-CLI Support",
        "CloudLinux CageFS Isolation & Imunify360",
      ],
    },
    {
      id: "webhosting-enterprise",
      slug: "enterprise-hosting",
      name: "Enterprise Hosting",
      displayName: "Enterprise Ultimate",
      unitPrice: 49.90,
      badge: "MAXIMALPOWER",
      shortDescription: "Dedizierter Ressourcenpool und VIP-Priorität für hochfrequentierte Portale und E-Commerce.",
      specs: { disk: "200 GB NVMe SSD", sites: "Unbegrenzt", ram: "4 GB RAM", cpu: "4 vCPU", mail: "Unbegrenzt", traffic: "Unlimitiert" },
      features: [
        "200 GB Enterprise NVMe SSD Speicher",
        "Unbegrenzte Domains & Websites",
        "Höchste I/O Priorität (100 MB/s Durchsatz)",
        "Unbegrenzte E-Mail Konten & Postfächer",
        "Unbegrenzte MariaDB Datenbanken",
        "Kostenlose Wildcard SSL-Zertifikate",
        "Tägliche automatisierte Backups (30 Tage)",
        "Redis Object Cache & Memcached integriert",
        "24/7 VIP Notfall-Support & 99.95% SLA",
        "Georedundantes Schweizer Backup",
      ],
    },
    {
      id: "reseller-hosting-basic",
      slug: "reseller-hosting",
      name: "Reseller Hosting",
      displayName: "Reseller Hosting Starter",
      unitPrice: 79.90,
      badge: "AGENTUREN",
      shortDescription: "Erstellen Sie eigene Hosting-Tarife für Ihre Kunden und verwalten Sie alles unter Ihrem eigenen Brand.",
      specs: { disk: "250 GB NVMe SSD", sites: "25 cPanel Konten", ram: "Multi-User", cpu: "Multi-Core", mail: "Unbegrenzt", traffic: "Unlimitiert" },
      features: [
        "250 GB NVMe SSD Speicherplatz",
        "25 eigenständige cPanel Kunden-Accounts",
        "Web Host Manager (WHM) Vollzugriff",
        "100% White-Label mit eigenen Nameservern",
        "Overselling aktiviert & flexible Quoten",
        "CloudLinux Konten-Isolation (CageFS)",
        "Kostenlose SSL-Zertifikate für jeden Kunden",
        "Tägliche automatisierte Backups",
        "100% Serverstandort Schweiz (Zürich)",
      ],
    },
  ],
  wordpress: [
    {
      id: "wp-starter",
      slug: "wordpress-hosting",
      name: "WordPress Hosting",
      displayName: "WordPress Fast WP",
      unitPrice: 6.90,
      badge: "WP OPTIMIERT",
      shortDescription: "Blitzschnelles WordPress-Hosting mit vorinstalliertem LiteSpeed Cache und automatischen Sicherheitsupdates.",
      specs: { disk: "25 GB NVMe SSD", sites: "1 WP Site", ram: "2 GB RAM", cpu: "2 vCPU", mail: "15 Postfächer", traffic: "Unlimitiert" },
      features: [
        "25 GB NVMe PCIe 4.0 SSD",
        "1 WordPress Installation inklusive",
        "LiteSpeed Cache & AccelerateWP vorinstalliert",
        "1-Klick Staging-Umgebung zum risikofreien Testen",
        "Automatische Core-, Plugin- & Theme-Updates",
        "Imunify360 KI-Malware-Schutz & WAF",
        "Kostenloses SSL-Zertifikat mit HTTP/3",
        "Tägliche automatische WordPress-Backups",
        "WP-CLI & SFTP/SSH Zugang",
      ],
    },
    {
      id: "wp-pro",
      slug: "wordpress-professional",
      name: "WordPress Professional",
      displayName: "WordPress Pro Boost",
      unitPrice: 8.90,
      badge: "BESTSELLER",
      isHighlight: true,
      shortDescription: "Mehrere WordPress-Websites betreiben mit Redis Object Cache und doppelter Rechenleistung.",
      specs: { disk: "60 GB NVMe SSD", sites: "5 WP Sites", ram: "4 GB RAM", cpu: "3 vCPU", mail: "50 Postfächer", traffic: "Unlimitiert" },
      features: [
        "60 GB NVMe PCIe 4.0 SSD",
        "Bis zu 5 WordPress Websites verwalten",
        "Dedizierter Redis Object Cache aktiviert",
        "LiteSpeed Web Server Enterprise",
        "Staging-Tool mit 1-Klick Push-to-Live",
        "Tägliche Cloud-Backups mit 30 Tagen Historie",
        "Erweiterte PHP Memory Limit (1024 MB)",
        "Smart Updates mit visueller Vorher-Nachher-Prüfung",
        "Prioritärer WordPress-Experten-Support",
      ],
    },
    {
      id: "wp-enterprise",
      slug: "wordpress-enterprise",
      name: "WordPress Enterprise / WooCommerce",
      displayName: "WooCommerce Ultra",
      unitPrice: 12.90,
      badge: "E-COMMERCE READY",
      shortDescription: "Für hochfrequentierte WooCommerce-Onlineshops mit maximalen Datenbank- und PHP-Ressourcen.",
      specs: { disk: "150 GB NVMe SSD", sites: "Unbegrenzt", ram: "8 GB RAM", cpu: "4 vCPU", mail: "Unbegrenzt", traffic: "Unlimitiert" },
      features: [
        "150 GB Samsung Enterprise NVMe SSD",
        "Unbegrenzte WordPress- & WooCommerce-Shops",
        "Höchste MySQL-Abfrage-Geschwindigkeit (NVMe RAID-10)",
        "Garantierte 8 GB RAM & 4 vCPU Ressourcen",
        "Redis Object Cache & OPcache Pro voraktiviert",
        "Unbegrenzte Wildcard SSL-Zertifikate",
        "Echtzeit-Sicherheitsüberwachung & Quarantäne",
        "24/7 VIP SLA Reaktionszeit < 30 Min.",
      ],
    },
  ],
  vps: [
    {
      id: "vps-mini",
      slug: "vps-mini",
      name: "Premium Mini VDS",
      displayName: "Premium Mini VDS",
      category: "vps",
      unitPrice: 19.90,
      originalPrice: 39.90,
      badge: "KLEINE PROJEKTE",
      isHighlight: false,
      shortDescription: "Intel Xeon Gold 6230R, Enterprise NVMe SSD und 1 Gbit/s Port. Ideal für Einstieg und kleinere Projekte.",
      specs: {
        cores: 3,
        cpu: "3 Core Gold 6230R",
        ram: "6 GB DDR4 ECC",
        disk: "40 GB NVMe SSD",
        port: "1 Gbit/s",
        traffic: "Unlimitiert",
        sites: "Root Zugriff",
        mail: "Eigenes Setup",
      },
      features: [
        "3 Core Intel Xeon Gold 6230R",
        "6 GB DDR4 ECC RAM",
        "40 GB Enterprise NVMe SSD",
        "1 Gbit/s Port & Unlimitierter Traffic",
        "Voller Root / SSH Zugriff & VNC Konsole",
        "1 Dedizierte statische Schweizer IPv4 + /64 IPv6",
        "DDoS-Schutz bis zu 1 Tbit/s inklusive",
        "Linux (Debian, Ubuntu, AlmaLinux, Rocky) oder Windows",
        "Sofortige automatisierte Bereitstellung",
      ],
    },
    {
      id: "vps-standard",
      slug: "vps-standard",
      name: "Premium Standard VDS",
      displayName: "Premium Standard VDS",
      category: "vps",
      unitPrice: 29.90,
      originalPrice: 59.90,
      badge: "POPULÄR",
      isHighlight: false,
      shortDescription: "Stabile KVM-Power mit 5 Kernen und 10 GB RAM für Agenturen und dynamische Portale.",
      specs: {
        cores: 5,
        cpu: "5 Core Gold 6230R",
        ram: "10 GB DDR4 ECC",
        disk: "125 GB NVMe SSD",
        port: "1 Gbit/s",
        traffic: "Unlimitiert",
        sites: "Root Zugriff",
        mail: "Eigenes Setup",
      },
      features: [
        "5 Core Intel Xeon Gold 6230R",
        "10 GB DDR4 ECC RAM",
        "125 GB Enterprise NVMe SSD",
        "1 Gbit/s Port & Unlimitierter Traffic",
        "Voller Root-Zugriff & API-Steuerung",
        "1 Dedizierte Schweizer IPv4 + IPv6 Subnetz",
        "Kostenlose Snapshots & automatisierte Backups",
        "Enterprise DDoS-Shield der Extraklasse",
        "Optional mit cPanel/WHM oder Plesk Lizenz",
      ],
    },
    {
      id: "vps-power",
      slug: "vps-power",
      name: "Premium Power VDS",
      displayName: "Premium Power VDS",
      category: "vps",
      unitPrice: 49.90,
      originalPrice: 89.90,
      badge: "★ EMPFOHLEN",
      isHighlight: true,
      shortDescription: "MeoHost Bestseller: 7 Kerne, 14 GB RAM und 150 GB NVMe für geschäftskritische Workloads.",
      specs: {
        cores: 7,
        cpu: "7 Core Gold 6230R",
        ram: "14 GB DDR4 ECC",
        disk: "150 GB NVMe SSD",
        port: "1 Gbit/s",
        traffic: "Unlimitiert",
        sites: "Root Zugriff",
        mail: "Eigenes Setup",
      },
      features: [
        "7 Core Intel Xeon Gold 6230R",
        "14 GB DDR4 ECC RAM",
        "150 GB Enterprise NVMe SSD",
        "1 Gbit/s redundante Anbindung & Unlimitierter Traffic",
        "Dedizierte Schweizer IP-Adressen",
        "KVM Vollvirtualisierung ohne Overselling",
        "Tägliche automatisierte Server-Snapshots",
        "Hardware DDoS Shield bis zu 1 Tbit/s",
        "24/7 VIP SLA Reaktionszeit < 30 Min.",
      ],
    },
    {
      id: "vps-ultra",
      slug: "vps-ultra",
      name: "Premium Ultra VDS",
      displayName: "Premium Ultra VDS",
      category: "vps",
      unitPrice: 79.90,
      originalPrice: 129.90,
      badge: "ENTERPRISE",
      isHighlight: false,
      shortDescription: "10 dedizierte Kerne und 20 GB RAM für anspruchsvolle Entwickler, SaaS und Datenbanken.",
      specs: {
        cores: 10,
        cpu: "10 Core Gold 6230R",
        ram: "20 GB DDR4 ECC",
        disk: "200 GB NVMe SSD",
        port: "1 Gbit/s",
        traffic: "Unlimitiert",
        sites: "Root Zugriff",
        mail: "Eigenes Setup",
      },
      features: [
        "10 Core Intel Xeon Gold 6230R",
        "20 GB DDR4 ECC RAM",
        "200 GB Enterprise NVMe SSD Storage",
        "1 Gbit/s ungedrosselte Glasfaser",
        "Dedizierte Schweizer IP-Adressen",
        "Tägliche automatisierte Backups",
        "24/7 Notfall-Support mit Schweizer Technikern",
        "99.99% Hardware-Verfügbarkeits-SLA",
      ],
    },
    {
      id: "vps-mega",
      slug: "vps-mega",
      name: "Premium Mega VDS",
      displayName: "Premium Mega VDS",
      category: "vps",
      unitPrice: 119.90,
      originalPrice: 189.90,
      badge: "HIGH PERFORMANCE",
      isHighlight: false,
      shortDescription: "14 Kerne und 28 GB RAM für Multi-Tenant-Setups, High-Traffic Plattformen und Big Data.",
      specs: {
        cores: 14,
        cpu: "14 Core Gold 6230R",
        ram: "28 GB DDR4 ECC",
        disk: "300 GB NVMe SSD",
        port: "1 Gbit/s",
        traffic: "Unlimitiert",
        sites: "Root Zugriff",
        mail: "Eigenes Setup",
      },
      features: [
        "14 Core Intel Xeon Gold 6230R",
        "28 GB DDR4 ECC RAM",
        "300 GB Enterprise NVMe SSD",
        "1 Gbit/s Port & Unlimitierter Traffic",
        "Volle Schweizer IP-Konfiguration",
        "Hardware RAID-10 Controller",
        "99.99% Hardware-Verfügbarkeits-SLA",
        "24/7 VIP Reaktionszeit < 30 Min.",
      ],
    },
    {
      id: "vps-god",
      slug: "vps-god",
      name: "Premium God VDS",
      displayName: "Premium God VDS",
      category: "vps",
      unitPrice: 169.90,
      originalPrice: 269.90,
      badge: "MAXIMAL POWER",
      isHighlight: false,
      shortDescription: "Das absolute Flaggschiff: 20 Kerne, 48 GB RAM und 350 GB NVMe für maximale Rechenleistung.",
      specs: {
        cores: 20,
        cpu: "20 Core Gold 6230R",
        ram: "48 GB DDR4 ECC",
        disk: "350 GB NVMe SSD",
        port: "1 Gbit/s",
        traffic: "Unlimitiert",
        sites: "Root Zugriff",
        mail: "Eigenes Setup",
      },
      features: [
        "20 Core Intel Xeon Gold 6230R (40 Threads)",
        "48 GB DDR4 ECC RAM",
        "350 GB Samsung Enterprise NVMe SSD",
        "1 Gbit/s ungedrosselte Glasfaser-Anbindung",
        "Unlimitierter Traffic ohne Drosselung",
        "Dedizierte Schweizer IP-Adressen",
        "Tägliche Snapshots & Disaster Recovery",
        "24/7 Dedicated Support Hotline",
        "Maximale Rechenleistung für Großprojekte",
      ],
    },
  ],
  reseller: [
    {
      id: "reseller-starter",
      slug: "reseller-hosting",
      name: "Reseller Hosting",
      displayName: "Reseller WHM Starter",
      unitPrice: 79.90,
      badge: "STARTUP AGENTUR",
      shortDescription: "Das Komplettpaket für Webdesigner und Freelancer, die ihren Kunden Hosting anbieten möchten.",
      specs: { disk: "250 GB NVMe SSD", sites: "25 Accounts", ram: "CageFS isoliert", cpu: "Multi-Core", mail: "Unbegrenzt", traffic: "Unlimitiert" },
      features: [
        "250 GB Samsung NVMe PCIe 4.0 Speicher",
        "Bis zu 25 eigenständige cPanel Kunden-Accounts",
        "Web Host Manager (WHM) Vollverwaltung",
        "100% White-Label: Eigene Nameserver (ns1.ihredomain.ch)",
        "Overselling aktiviert für maximale Flexibilität",
        "CloudLinux CageFS Schutz pro Mandant",
        "Automatische SSL-Zertifikate für alle Kunden",
        "1-Klick Installer Softaculous für alle Accounts",
      ],
    },
    {
      id: "reseller-pro-plan",
      slug: "reseller-pro",
      name: "Reseller Hosting Pro",
      displayName: "Reseller WHM Business",
      unitPrice: 129.90,
      badge: "PRO AGENTUR",
      isHighlight: true,
      shortDescription: "Für wachsende Agenturen mit hohem Speicherbedarf und vielen anspruchsvollen Kundenprojekten.",
      specs: { disk: "500 GB NVMe SSD", sites: "50 Accounts", ram: "CageFS isoliert", cpu: "Multi-Core", mail: "Unbegrenzt", traffic: "Unlimitiert" },
      features: [
        "500 GB Enterprise NVMe SSD Speicher",
        "Bis zu 50 cPanel Kunden-Accounts",
        "WHM Vollzugriff mit erweiterten Rechten",
        "1 Dedizierte IP-Adresse inklusive",
        "LiteSpeed Web Server Beschleunigung",
        "Unbegrenzte Domains, E-Mails & Datenbanken",
        "Tägliche automatisierte Backups aller Accounts",
        "Bevorzugter Agentur-Support per Direktdurchwahl",
      ],
    },
  ],
  mail: [
    {
      id: "mail-starter",
      slug: "business-mail-hosting",
      name: "Business Mail Hosting",
      displayName: "E-Mail Starter (5 Postfächer)",
      unitPrice: 4.90,
      badge: "BASIS",
      shortDescription: "Professionelle Firmen-E-Mails mit Ihrer eigenen Domain. Zuverlässig, werbefrei und sicher.",
      specs: { disk: "25 GB Speicher", sites: "Eigene Domain", ram: "Cloud Mail", cpu: "Cloud Mail", mail: "5 Konten (je 5GB)", traffic: "Unlimitiert" },
      features: [
        "5 professionelle Postfächer (z.B. info@ihrefirma.ch)",
        "25 GB Gesamtspeicherplatz (5 GB pro Postfach)",
        "Webmail (Roundcube) mit moderner Benutzeroberfläche",
        "Kompatibel mit Outlook, Apple Mail, Thunderbird & Mobile",
        "IMAP, POP3, SMTP mit SSL/TLS Verschlüsselung",
        "Spam- & Virenschutz mit täglichen Filter-Updates",
        "Autoresponder, Aliase & Weiterleitungen inklusive",
      ],
    },
    {
      id: "mail-business-plan",
      slug: "mail-business",
      name: "Mail Business",
      displayName: "E-Mail Business (20 Postfächer)",
      unitPrice: 14.90,
      badge: "KMU FAVORIT",
      isHighlight: true,
      shortDescription: "Erweiterte Postfächer mit ActiveSync für Echtzeit-Synchronisation von Mails, Kalender und Kontakten.",
      specs: { disk: "100 GB Speicher", sites: "Eigene Domain", ram: "Cloud Mail", cpu: "Cloud Mail", mail: "20 Konten (je 5-15GB)", traffic: "Unlimitiert" },
      features: [
        "20 professionelle Postfächer mit flexibler Speicherverteilung",
        "100 GB Gesamtspeicher auf schnellen NVMe-Platten",
        "Exchange ActiveSync (EAS) für iPhone, Android & Outlook",
        "SpamExperts Anti-Spam & Anti-Virus Gateway",
        "DKIM, SPF & DMARC Einrichtungsassistent für 100% Zustellbarkeit",
        "Gemeinsame Firmen-Kalender & Adressbücher (CalDAV/CardDAV)",
        "Tägliche Schweizer E-Mail-Datensicherung",
      ],
    },
    {
      id: "mail-corp",
      slug: "mail-corporate",
      name: "Mail Corporate",
      displayName: "E-Mail Enterprise Suite",
      unitPrice: 29.90,
      badge: "ENTERPRISE",
      shortDescription: "Unbegrenzte Postfächer mit gesetzeskonformer Schweizer E-Mail-Archivierung und VIP-Zustellbarkeitsgarantie.",
      specs: { disk: "250 GB Speicher", sites: "Mehrere Domains", ram: "Cloud Mail", cpu: "Cloud Mail", mail: "Unbegrenzt", traffic: "Unlimitiert" },
      features: [
        "Unbegrenzte Firmen-Postfächer anlegen",
        "250 GB NVMe Mail-Speicherplatz",
        "Rechtssichere Schweizer E-Mail-Archivierung (10 Jahre)",
        "Dedizierte IP-Adresse für ausgehenden E-Mail-Versand",
        "SpamExperts Premium Outbound-Relay",
        "24/7 Notfall-Support & 99.99% Mail-Uptime-Garantie",
      ],
    },
  ],
  ssl: [
    {
      id: "ssl-free",
      slug: "ssl-free",
      name: "Let's Encrypt Auto-SSL",
      displayName: "Let's Encrypt SSL",
      unitPrice: 0.0,
      badge: "KOSTENLOS INKLUSIVE",
      shortDescription: "Automatische Domain-Validierung (DV) für alle Domains und Subdomains auf Ihrem RedWORK Hosting.",
      specs: { disk: "256-Bit SSL", sites: "Alle Domains", ram: "Kostenlos", cpu: "Kostenlos", mail: "Inklusive", traffic: "Kostenlos" },
      features: [
        "Kostenlos in jedem RedWORK Hosting-Paket enthalten",
        "Domain-Validierung (DV) in Echtzeit",
        "256-Bit Industriestandard-Verschlüsselung",
        "Automatische Verlängerung alle 90 Tage ohne Aufwand",
        "Volle Browser- und Smartphone-Kompatibilität",
      ],
    },
    {
      id: "ssl-dv",
      slug: "ssl-basic",
      name: "SSL Basic (DV)",
      displayName: "Sectigo PositiveSSL (DV)",
      unitPrice: 25.0,
      badge: "GÜNSTIG",
      shortDescription: "Gewerbliches SSL-Zertifikat mit Garantie und offiziellem Vertrauenssiegel für Ihre Website.",
      specs: { disk: "CHF 50'000 Garantie", sites: "1 Domain", ram: "Zertifikat", cpu: "Zertifikat", mail: "Support", traffic: "1 Jahr Gültigkeit" },
      features: [
        "Sichert eine Hauptdomain (z.B. www.ihrefirma.ch)",
        "CHF 50'000.- Haftungsgarantie der Zertifizierungsstelle",
        "Statisches Vertrauenssiegel (Site Seal) für die Website",
        "Schnelle Ausstellung innert 10 Minuten",
        "Strikte 256-Bit SSL/TLS Verschlüsselung",
      ],
    },
    {
      id: "ssl-ov",
      slug: "ssl-professional",
      name: "SSL Professional (OV)",
      displayName: "Sectigo Wildcard SSL",
      unitPrice: 75.0,
      badge: "BELIEBT",
      isHighlight: true,
      shortDescription: "Sichert Ihre Hauptdomain und unbegrenzt alle Subdomains (*.ihrefirma.ch) mit einem einzigen Zertifikat.",
      specs: { disk: "CHF 250'000 Garantie", sites: "*.ihredomain.ch", ram: "Zertifikat", cpu: "Zertifikat", mail: "Support", traffic: "1 Jahr Gültigkeit" },
      features: [
        "Sichert *.ihrefirma.ch und alle zukünftigen Subdomains",
        "Organisation Validierung (OV) oder Wildcard DV",
        "CHF 250'000.- Haftungsgarantie",
        "Dynamisches Echtzeit-Vertrauenssiegel von Sectigo",
        "Höchste Sicherheit für E-Commerce & Kundenportale",
      ],
    },
    {
      id: "ssl-ev",
      slug: "ssl-enterprise",
      name: "SSL Enterprise (EV)",
      displayName: "Sectigo EV Green Bar",
      unitPrice: 150.0,
      badge: "MAXIMALE SICHERHEIT",
      shortDescription: "Extended Validation (EV) mit strenger Unternehmensprüfung und maximalem Vertrauen für Banken & Grosskunden.",
      specs: { disk: "CHF 1'750'000 Garantie", sites: "EV Geprüft", ram: "Zertifikat", cpu: "Zertifikat", mail: "Support", traffic: "1 Jahr Gültigkeit" },
      features: [
        "Vollständige juristische Unternehmensprüfung (EV)",
        "Anzeige des Firmennamens im Zertifikat",
        "CHF 1'750'000.- maximale Haftungsgarantie",
        "Höchste Konversionsrate für Onlineshops und Finanzdienstleister",
        "Schutz vor Phishing und Markenmissbrauch",
      ],
    },
  ],
};

// ============================================================================
// 2. DOMAIN TLD PRICE LIST (Jrodix Style)
// ============================================================================
const TLD_PRICING_TABLE = [
  { tld: ".ch", reg: 15.00, renew: 15.00, transfer: 15.00, badge: "SCHWEIZ TOP-SELLER", highlight: true },
  { tld: ".com", reg: 12.00, renew: 14.00, transfer: 12.00, badge: "WELTWEIT #1" },
  { tld: ".de", reg: 8.00, renew: 8.00, transfer: 8.00, badge: "DEUTSCHLAND" },
  { tld: ".net", reg: 16.00, renew: 18.00, transfer: 16.00, badge: "NETZWERK" },
  { tld: ".org", reg: 17.00, renew: 19.00, transfer: 17.00, badge: "ORGANISATIONEN" },
  { tld: ".li", reg: 19.00, renew: 19.00, transfer: 19.00, badge: "LIECHTENSTEIN" },
  { tld: ".eu", reg: 14.00, renew: 14.00, transfer: 14.00, badge: "EUROPA" },
  { tld: ".at", reg: 18.00, renew: 18.00, transfer: 18.00, badge: "ÖSTERREICH" },
  { tld: ".info", reg: 19.00, renew: 22.00, transfer: 19.00, badge: "INFORMATIONS-PORTALE" },
  { tld: ".io", reg: 45.00, renew: 49.00, transfer: 45.00, badge: "TECH & STARTUPS" },
];

// ============================================================================
// 3. COMPARISON MATRIX (6 Categories)
// ============================================================================
const COMPARISON_SECTIONS = [
  {
    category: "Speicher & Performance",
    rows: [
      { label: "NVMe SSD Speicherplatz", values: ["20 GB", "50 GB", "200 GB", "250 GB"] },
      { label: "Garantierter RAM", values: ["1 GB", "2 GB", "4 GB", "8 GB"] },
      { label: "vCPU Rechenleistung", values: ["1 vCPU", "2 vCPU", "4 vCPU", "4 vCPU"] },
      { label: "Webserver-Technologie", values: ["LiteSpeed + LSCache", "LiteSpeed Enterprise", "LiteSpeed Dedicated", "LiteSpeed WHM"] },
      { label: "Monatlicher Traffic", values: ["Unlimitiert (Fair Use)", "Unlimitiert", "Unlimitiert", "Unlimitiert"] },
      { label: "Anbindung Rechenzentrum", values: ["40 Gbit/s redundantes Backbone", "40 Gbit/s redundantes Backbone", "40 Gbit/s redundantes Backbone", "40 Gbit/s redundantes Backbone"] },
    ],
  },
  {
    category: "Websites & Domains",
    rows: [
      { label: "Verwaltbare Websites / Domains", values: ["1 Domain", "5 Domains", "Unbegrenzt", "25 Kunden-Accounts"] },
      { label: "Subdomains", values: ["Unbegrenzt", "Unbegrenzt", "Unbegrenzt", "Unbegrenzt"] },
      { label: "DNS-Zone Editor", values: [true, true, true, true] },
      { label: "Domain-Aliase & Weiterleitungen", values: [true, true, true, true] },
    ],
  },
  {
    category: "E-Mail & Kommunikation",
    rows: [
      { label: "E-Mail Postfächer", values: ["10 Postfächer", "50 Postfächer", "Unbegrenzt", "Unbegrenzt"] },
      { label: "Speicher pro Postfach", values: ["5 GB", "10 GB", "Flexibel wählbar", "Flexibel wählbar"] },
      { label: "Webmail, IMAP, POP3 & SMTP", values: [true, true, true, true] },
      { label: "Spamschutz & Antivirus (Imunify)", values: [true, true, true, true] },
      { label: "Autoresponder & E-Mail Weiterleitung", values: [true, true, true, true] },
    ],
  },
  {
    category: "Entwicklung & Datenbanken",
    rows: [
      { label: "MariaDB / MySQL Datenbanken", values: ["2 Datenbanken", "10 Datenbanken", "Unbegrenzt", "Unbegrenzt"] },
      { label: "phpMyAdmin Verwaltung", values: [true, true, true, true] },
      { label: "PHP-Versionen (7.4, 8.1, 8.2, 8.3)", values: [true, true, true, true] },
      { label: "SSH & Git-Zugang", values: [false, true, true, true] },
      { label: "WP-CLI & Staging-Umgebung", values: [false, true, true, true] },
      { label: "Redis Object Cache", values: [false, "Optional", true, true] },
    ],
  },
  {
    category: "Sicherheit & Backups",
    rows: [
      { label: "Kostenloses SSL-Zertifikat", values: ["Let's Encrypt", "Let's Encrypt Auto-SSL", "Let's Encrypt Wildcard", "Auto-SSL alle Mandanten"] },
      { label: "Tägliche automatische Backups", values: ["7 Tage Retention", "14 Tage Retention", "30 Tage Retention", "14 Tage Retention"] },
      { label: "1-Klick Backup-Wiederherstellung", values: [true, true, true, true] },
      { label: "DDoS-Schutz (Multi-Layer)", values: [true, true, true, true] },
      { label: "CloudLinux CageFS Isolation", values: [true, true, true, true] },
    ],
  },
  {
    category: "Schweizer Service & SLA",
    rows: [
      { label: "Standort Rechenzentrum", values: ["Schweiz (Zürich)", "Schweiz (Zürich)", "Schweiz (Zürich/Luzern)", "Schweiz (Zürich)"] },
      { label: "Schweizer Datenschutz (DSG)", values: [true, true, true, true] },
      { label: "Verfügbarkeits-Garantie (SLA)", values: ["99.9% Uptime", "99.9% Uptime", "99.95% VIP Uptime", "99.9% Uptime"] },
      { label: "Support-Level", values: ["E-Mail & Tickets", "Prioritärer Support", "24/7 VIP Notfall", "Agentur-Partner"] },
    ],
  },
];

export default function HostingLanding() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, login: setAuthUser } = useAuth();
  const { openQuote, openContact } = useModals();

  // Read URL query parameter ?category=... or ?tab=...
  const categoryFromUrl = searchParams.get("category") || searchParams.get("tab");

  // Active Category Tab
  const [activeCategory, setActiveCategory] = useState(
    categoryFromUrl && HOSTING_CATEGORIES.some((c) => c.id === categoryFromUrl)
      ? categoryFromUrl
      : "webhosting"
  );

  useEffect(() => {
    if (categoryFromUrl && HOSTING_CATEGORIES.some((c) => c.id === categoryFromUrl)) {
      if (activeCategory !== categoryFromUrl) {
        setActiveCategory(categoryFromUrl);
      }
    }
  }, [categoryFromUrl, activeCategory]);

  const handleSelectCategory = (catId) => {
    if (activeCategory !== catId) {
      setActiveCategory(catId);
    }
    setSearchParams({ category: catId }, { replace: true });
  };

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState("monthly"); // monthly | yearly
  const [activeFaq, setActiveFaq] = useState(null);

  // Domain search state
  const [domainSearchInput, setDomainSearchInput] = useState("");
  const [selectedTld, setSelectedTld] = useState("ch");
  const [domainSearching, setDomainSearching] = useState(false);
  const [domainSearchResult, setDomainSearchResult] = useState(null);

  // Integrated Checkout Modal State
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState(1); // 1: Paket, 2: Domain, 3: Kunde, 4: Übersicht, 5: Erfolgreich
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [orderCycle, setOrderCycle] = useState("monthly");
  const [domainChoice, setDomainChoice] = useState("new"); // new | transfer | own | later
  const [domainName, setDomainName] = useState("");
  const [modalDomainQuery, setModalDomainQuery] = useState("");
  const [modalDomainTld, setModalDomainTld] = useState(".ch");
  const [modalDomainChecking, setModalDomainChecking] = useState(false);
  const [modalDomainResult, setModalDomainResult] = useState(null);
  const [modalAuthCode, setModalAuthCode] = useState("");
  const [orderRef, setOrderRef] = useState("");
  const [orderRefCopied, setOrderRefCopied] = useState(false);
  const [twintCopied, setTwintCopied] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("twint"); // twint | invoice | card
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [orderError, setOrderError] = useState("");
  const [showCustomerAuthModal, setShowCustomerAuthModal] = useState(false);
  const [orderSuccessData, setOrderSuccessData] = useState(null);

  // Inline Auth state for checkout
  const [authMode, setAuthMode] = useState("register"); // register | login
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authFirstName, setAuthFirstName] = useState("");
  const [authLastName, setAuthLastName] = useState("");
  const [authCompany, setAuthCompany] = useState("");
  const [authPhone, setAuthPhone] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await api.get("/products");
      setProducts(res.data || []);
    } catch (err) {
      console.warn("Konnte Produkte nicht laden, Fallbacks aktiv:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Webhosting Schweiz | High-Speed NVMe Hosting & Cloud Server | RedWORK.ch";
    fetchProducts();
  }, []);

  // Compute active category plans directly from DB products (with admin updates & real-time additions)
  const activePlans = useMemo(() => {
    const fallbackList = CATEGORY_PRODUCTS[activeCategory] || CATEGORY_PRODUCTS.webhosting;
    if (!products || products.length === 0) return fallbackList;

    // Filter active DB products matching activeCategory
    const dbMatched = products.filter((p) => {
      if (p.status === "inactive") return false;
      const cat = (p.category || "").toLowerCase();
      const target = activeCategory.toLowerCase();
      if (cat === target) return true;
      if (target === "domains" && cat === "domain") return true;
      if (target === "webhosting" && (!cat || cat === "webspace" || cat === "hosting")) return true;
      return false;
    });

    if (dbMatched.length > 0) {
      return dbMatched.map((p) => {
        const fb = fallbackList.find(
          (f) =>
            (p.slug && f.slug === p.slug) ||
            (f.name && p.name && f.name.toLowerCase() === p.name.toLowerCase())
        );

        return {
          id: p.id || String(p._id),
          slug: p.slug || p.id,
          name: p.name,
          displayName: p.displayName || p.name,
          unitPrice: Number(p.unitPrice ?? p.price ?? (fb ? fb.unitPrice : 0)),
          badge: p.badge || (fb ? fb.badge : ""),
          isHighlight: Boolean(p.isHighlight ?? (fb ? fb.isHighlight : false)),
          shortDescription: p.shortDescription || p.description || (fb ? fb.shortDescription : ""),
          specs: p.specs && Object.keys(p.specs).length > 0 ? p.specs : (fb ? fb.specs : {}),
          features: Array.isArray(p.features) && p.features.length > 0 ? p.features : (fb ? fb.features : []),
          category: p.category || activeCategory,
        };
      });
    }

    // Fallback if DB category has no products
    return fallbackList.map((item) => {
      const matched = products.find(
        (p) =>
          (p.slug && p.slug === item.slug) ||
          (p.id && p.id === item.id) ||
          (p.name && item.name && p.name.toLowerCase().includes(item.name.toLowerCase()))
      );
      if (matched) {
        return {
          ...item,
          id: matched.id || String(matched._id) || item.id,
          unitPrice: Number(matched.unitPrice ?? matched.price ?? item.unitPrice),
        };
      }
      return item;
    });
  }, [activeCategory, products]);

  // Main 4 Webhosting Plans for the comparison table
  const mainWebhostingPlans = useMemo(() => {
    return CATEGORY_PRODUCTS.webhosting.map((item) => {
      const matched = products.find(
        (p) =>
          (p.slug && p.slug === item.slug) ||
          (p.name && item.name && p.name.toLowerCase().includes(item.name.toLowerCase()))
      );
      if (matched) {
        return {
          ...item,
          id: matched.id || item.id,
          unitPrice: Number(matched.unitPrice ?? matched.price ?? item.unitPrice),
        };
      }
      return item;
    });
  }, [products]);

  // Handle Domain Search
  const handleDomainCheck = async (e) => {
    if (e) e.preventDefault();
    let query = domainSearchInput.trim().toLowerCase();
    if (!query) return;

    if (!query.includes(".")) {
      query = `${query}.${selectedTld}`;
    }

    setDomainSearching(true);
    setDomainSearchResult(null);
    try {
      const res = await api.get(`/domains/check?domain=${encodeURIComponent(query)}`);
      setDomainSearchResult(res.data);
    } catch (err) {
      setDomainSearchResult({
        domain: query,
        available: false,
        message: "Abfrage vorübergehend nicht möglich.",
        price: 15.0,
      });
    } finally {
      setDomainSearching(false);
    }
  };

  // Handle Domain check inside modal
  const handleModalDomainCheck = async (explicitDomain = null) => {
    let raw = explicitDomain !== null && typeof explicitDomain === "string" ? explicitDomain : modalDomainQuery;
    let query = (raw || "").trim().toLowerCase();
    if (!query) {
      setOrderError("Bitte Domainname eingeben (z.B. meine-firma).");
      return;
    }
    query = query.replace(/^https?:\/\//, "").split("/")[0].trim();
    if (!query.includes(".")) {
      const tld = modalDomainTld.startsWith(".") ? modalDomainTld : `.${modalDomainTld}`;
      query = `${query}${tld}`;
    }

    setModalDomainChecking(true);
    setOrderError("");
    setModalDomainResult(null);

    try {
      const res = await api.get(`/domains/check?domain=${encodeURIComponent(query)}`);
      setModalDomainResult(res.data);
      if (res.data.available) {
        setDomainName(res.data.domain);
      }
    } catch (err) {
      console.error("Domain-Check Fehler:", err);
      setOrderError("Fehler bei der Domainprüfung. Bitte versuchen Sie es erneut.");
    } finally {
      setModalDomainChecking(false);
    }
  };

  // Launch checkout modal with specific plan
  const getOrderTotalAmount = (prod, cycle) => {
    if (!prod) return "0.00";
    const price = Number(prod.unitPrice) || 0;
    if (isAnnualOnlyProduct(prod)) {
      return price.toFixed(2);
    }
    if (cycle === "yearly") {
      return (price * 12 * 0.9).toFixed(2);
    }
    return price.toFixed(2);
  };

  const openOrderModal = (plan, prefilledDomain = "") => {
    const generatedRef = `RW-2026-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    setOrderRef(generatedRef);
    setOrderRefCopied(false);
    setTwintCopied(false);
    setPaymentMethod("twint");
    setSelectedProduct(plan);
    const isAnnual = isAnnualOnlyProduct(plan);
    setOrderCycle(isAnnual ? "yearly" : billingCycle);
    setCheckoutStep(1);
    setOrderError("");
    setOrderSuccessData(null);
    setModalAuthCode("");

    if (prefilledDomain) {
      setDomainChoice("new");
      setDomainName(prefilledDomain);
      setModalDomainQuery(prefilledDomain.includes(".") ? prefilledDomain.split(".")[0] : prefilledDomain);
      const parts = prefilledDomain.split(".");
      if (parts.length > 1) {
        setModalDomainTld(`.${parts.slice(1).join(".")}`);
      }
      handleModalDomainCheck(prefilledDomain);
    } else {
      setDomainChoice("new");
      setDomainName("");
      setModalDomainQuery("");
      setModalDomainTld(".ch");
      setModalDomainResult(null);
    }
    setShowCheckoutModal(true);
  };

  // Inline Auth Handler
  const handleInlineAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError("");
    try {
      if (authMode === "register") {
        if (!authFirstName || !authLastName || !authEmail || !authPassword) {
          setAuthError("Bitte alle Pflichtfelder ausfüllen.");
          setAuthLoading(false);
          return;
        }
        const res = await api.post("/auth/register", {
          firstName: authFirstName,
          lastName: authLastName,
          email: authEmail,
          password: authPassword,
          company: authCompany,
          phone: authPhone,
        });
        localStorage.setItem("token", res.data.access_token);
        if (setAuthUser) setAuthUser(res.data.user);
        setCheckoutStep(4);
      } else {
        const res = await api.post("/auth/login", {
          email: authEmail,
          password: authPassword,
        });
        localStorage.setItem("token", res.data.access_token);
        if (setAuthUser) setAuthUser(res.data.user);
        setCheckoutStep(4);
      }
    } catch (err) {
      setAuthError(err.response?.data?.detail || "Anmeldung fehlgeschlagen.");
    } finally {
      setAuthLoading(false);
    }
  };

  // Submit final order
  const handleFinalOrderSubmit = async () => {
    if (!selectedProduct) return;
    setOrderSubmitting(true);
    setOrderError("");

    const ref = orderRef || `RW-2026-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;

    try {
      const payload = {
        productId: selectedProduct.id,
        duration: orderCycle,
        quantity: 1,
        domainChoice,
        domainName: domainChoice !== "later" ? domainName : undefined,
        authCode: domainChoice === "transfer" ? modalAuthCode : undefined,
        addons: [],
        paymentMethod,
        reference: ref,
        contractNumber: ref,
      };

      const res = await api.post("/orders", payload);
      setOrderSuccessData(res.data);
      setCheckoutStep(5);
    } catch (err) {
      setOrderError(
        err.response?.data?.detail ||
          "Fehler beim Erstellen der Bestellung. Bitte überprüfen Sie Ihre Angaben."
      );
    } finally {
      setOrderSubmitting(false);
    }
  };

  // FAQs List
  const faqs = [
    {
      q: "Welches Hosting-Paket passt am besten zu mir?",
      a: "Für persönliche Websites, Blogs oder KMU-Auftritte reicht das Paket 'Linux NVMe Webspace' (ab CHF 9.90/Mt.). Für geschäftliche Auftritte mit mehreren Domains, Onlineshops und automatischen täglichen Backups empfehlen wir 'Business Hosting' (CHF 19.90/Mt.). Für maximale Leistung und WordPress-Portale steht 'WordPress Pro' oder 'Enterprise Hosting' bereit. Agenturen wählen 'Reseller Hosting' (ab CHF 79.90/Mt.) mit eigenem WHM und White-Label Nameservern.",
    },
    {
      q: "Kann ich meine bestehende Website kostenlos zu RedWORK umziehen?",
      a: "Ja, zu 100%! Unser Schweizer Technik-Team übernimmt den kompletten Umzug Ihrer Webdaten, Datenbanken und E-Mail-Postfächer von Ihrem bisherigen Provider völlig kostenlos und ohne jegliche Ausfallzeit.",
    },
    {
      q: "Wo genau befinden sich die Server und Rechenzentren?",
      a: "Unsere gesamte Hosting-Infrastruktur steht in zertifizierten Tier-3+ Schweizer Rechenzentren in Zürich und Basel. Sämtliche Daten unterliegen vollumfänglich dem strengen Schweizer Datenschutzgesetz (DSG) sowie den FINMA-Anforderungen.",
    },
    {
      q: "Wie funktioniert die Abrechnung und gibt es Rabatte?",
      a: "Sie können flexibel monatlich oder jährlich bezahlen. Bei jährlicher Abrechnung schenken wir Ihnen 10% Rabatt (entspricht fast 2 Gratismonaten!). Die Zahlung erfolgt bequem per Schweizer QR-Rechnung (zahlbar innert 30 Tagen), TWINT oder Kreditkarte.",
    },
    {
      q: "Sind automatische Backups und SSL-Zertifikate inklusive?",
      a: "Ja. Alle Pakete enthalten kostenlose Let's Encrypt SSL-Zertifikate mit automatischer Erneuerung sowie tägliche Backups (bis zu 30 Tage Aufbewahrung), die Sie im cPanel mit einem Klick selbstständig wiederherstellen können.",
    },
    {
      q: "Kann ich später problemlos auf ein grösseres Paket wechseln?",
      a: "Selbstverständlich. Ein Upgrade auf ein höheres Paket oder einen Cloud VPS ist jederzeit im Kundenportal mit wenigen Klicks ohne Unterbrechung oder Datenverlust möglich.",
    },
    {
      q: "Wie schnell wird mein Hosting-Account nach der Bestellung freigeschaltet?",
      a: "Die Bereitstellung Ihres Schweizer cPanel-Accounts erfolgt vollautomatisch unmittelbar nach Absenden Ihrer Bestellung binnen weniger Minuten per E-Mail.",
    },
    {
      q: "Welche PHP-Versionen und Entwickler-Tools werden unterstützt?",
      a: "Sie können zwischen PHP 7.4, 8.1, 8.2 und 8.3 frei wählen. Zudem stehen phpMyAdmin, MariaDB 10.6, Cronjobs, Node.js, Python und ab dem Business-Tarif SSH-, Git- und WP-CLI-Zugriff bereit.",
    },
    {
      q: "Was unterscheidet RedWORK von ausländischen Billighostern?",
      a: "RedWORK bietet echte Schweizer Infrastruktur mit garantierten NVMe Gen4 SSDs, LiteSpeed Enterprise Webservern, Imunify360 KI-Sicherheit und persönlichem Schweizer Support ohne Callcenter-Warteschleifen.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#07090F] text-white flex flex-col selection:bg-[#E63946] selection:text-white">
      <Header scrolled={true} />

      <main className="flex-1 pt-24 sm:pt-28 pb-20">
        {/* ================================================== */}
        {/* 1. HERO SECTION (Jrodix Style)                     */}
        {/* ================================================== */}
        <section className="relative overflow-hidden pt-8 pb-16 lg:py-24 border-b border-white/10">
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-red-950/20 via-transparent to-transparent pointer-events-none" />

          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
            {/* Pill Badge */}
            <div className="inline-flex items-center gap-2 rounded-full border border-[#E63946]/40 bg-[#E63946]/10 px-4 py-1.5 text-xs font-black uppercase tracking-widest text-[#E63946] mb-6 shadow-sm">
              <span className="flex h-2 w-2 rounded-full bg-[#E63946] animate-pulse" />
              <span>🇨🇭 Schweizer High-Speed Webhosting • 100% NVMe SSD Gen4</span>
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white max-w-4xl mx-auto leading-tight">
              Professionelles Webhosting mit garantierter Schweizer NVMe-Power
            </h1>

            <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto mt-5 leading-relaxed">
              Bis zu 84x schnellere Ladezeiten dank LiteSpeed Enterprise, automatische tägliche Backups,
              kostenloses SSL und kompromissloser Schweizer Datenschutz im ISO 27001 zertifizierten Rechenzentrum Zürich.
            </p>

            {/* Trust Points */}
            <div className="flex items-center justify-center gap-4 sm:gap-6 flex-wrap mt-8 text-xs sm:text-sm font-semibold text-slate-300">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={16} className="text-[#E63946]" />
                100% NVMe Gen4 Storage
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={16} className="text-[#E63946]" />
                99.9% Verfügbarkeits-SLA
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={16} className="text-[#E63946]" />
                LiteSpeed & LSCache
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={16} className="text-[#E63946]" />
                Schweizer Datenschutz (DSG)
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={16} className="text-[#E63946]" />
                30 Tage Geld-zurück-Garantie
              </span>
            </div>

            {/* Hero CTAs */}
            <div className="flex items-center justify-center gap-3 sm:gap-4 mt-8 sm:mt-10 flex-col sm:flex-row max-w-md sm:max-w-none mx-auto">
              <a
                href="#kategorien"
                className="w-full sm:w-auto px-6 sm:px-8 py-3.5 rounded-2xl bg-[#E63946] hover:bg-[#c5303d] text-white font-black text-xs sm:text-sm shadow-lg shadow-red-600/25 transition transform hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
              >
                <span>Alle Hosting-Pakete ansehen</span>
                <ArrowRight size={16} />
              </a>
              <a
                href="#domain-checker"
                className="w-full sm:w-auto px-6 sm:px-7 py-3.5 rounded-2xl bg-white/10 hover:bg-white/15 text-white font-bold text-xs sm:text-sm border border-white/15 transition inline-flex items-center justify-center gap-2"
              >
                <Search size={16} />
                <span>Wunschdomain prüfen</span>
              </a>
            </div>
          </div>
        </section>

        {/* ================================================== */}
        {/* 1.5 3D CINEMATIC HERO BANNER: WEBVIEW & APP NATIVE */}
        {/* ================================================== */}
        <section className="relative overflow-hidden py-12 sm:py-16 lg:py-20 border-b border-white/10 bg-gradient-to-b from-[#090C14] via-[#0D1220] to-[#07090F]">
          {/* Ambient Lighting & Cybernetic Grid */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_20%,rgba(230,57,70,0.18),transparent)] pointer-events-none" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              {/* Left Column: Description & Value Props (7 Cols) */}
              <div className="lg:col-span-7 space-y-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-[#E63946]/30 bg-[#E63946]/10 px-3.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#E63946] shadow-sm">
                  <Smartphone size={13} className="animate-pulse" />
                  <span>🇨🇭 Neuheit • Webview & Native App Transformation</span>
                </div>

                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight leading-tight">
                  Ihre Website & Cloud als native <span className="bg-gradient-to-r from-[#E63946] via-[#FF7A00] to-[#D4AF37] bg-clip-text text-transparent">iOS & Android App</span>
                </h2>

                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-normal">
                  Bringen Sie Ihr Schweizer Unternehmen direkt auf den Homescreen Ihrer Kunden! Wir konvertieren
                  Ihre bestehende oder neue Website in eine hochperformante native Smartphone-Applikation.
                  Inklusive Push-Benachrichtigungen, Offline-Screen und schlüsselfertiger Bereitstellung für den
                  Apple App Store und Google Play Store.
                </p>

                {/* Core App Highlights */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-2">
                  <div className="flex items-start gap-2.5 bg-white/[0.03] p-2.5 rounded-xl border border-white/5">
                    <div className="h-7 w-7 rounded-lg bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 flex items-center justify-center shrink-0 mt-0.5">
                      <Bell size={14} />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-white">Push-Benachrichtigungen</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">Kunden sofort per Push erreichen</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-2.5 bg-white/[0.03] p-2.5 rounded-xl border border-white/5">
                    <div className="h-7 w-7 rounded-lg bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 flex items-center justify-center shrink-0 mt-0.5">
                      <Zap size={14} />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-white">120 FPS Webview 2.0</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">Butterweiches Scrolling & native App-Optik</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-2.5 bg-white/[0.03] p-2.5 rounded-xl border border-white/5">
                    <div className="h-7 w-7 rounded-lg bg-amber-500/15 text-amber-400 border border-amber-500/30 flex items-center justify-center shrink-0 mt-0.5">
                      <Download size={14} />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-white">App Store & Play Store</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">Icons, Screenshots & Upload-Begleitung</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-2.5 bg-white/[0.03] p-2.5 rounded-xl border border-white/5">
                    <div className="h-7 w-7 rounded-lg bg-purple-500/15 text-purple-400 border border-purple-500/30 flex items-center justify-center shrink-0 mt-0.5">
                      <ShieldCheck size={14} />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-white">Keine doppelte Pflege</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">Änderungen auf der Website sofort in der App</div>
                    </div>
                  </div>
                </div>

                {/* Pricing Callout & Action Bar */}
                <div className="pt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <div className="rounded-xl bg-white/[0.04] p-2.5 px-4 border border-white/10 flex items-baseline gap-2 shrink-0">
                    <span className="text-[11px] text-slate-400 font-medium">Komplettpreis:</span>
                    <span className="text-lg sm:text-xl font-bold font-mono text-[#D4AF37]">Ab CHF 299.-</span>
                    <span className="text-[10px] text-slate-400 font-normal">einmalig</span>
                  </div>

                  <button
                    type="button"
                    onClick={openQuote}
                    className="flex-1 py-3 px-5 rounded-xl bg-gradient-to-r from-[#E63946] to-[#FF7A00] hover:from-[#c5303d] hover:to-[#e56d00] text-white font-semibold text-xs sm:text-sm shadow-lg shadow-red-600/25 transition transform hover:-translate-y-0.5 flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <Sparkles size={14} />
                    <span>Webview App jetzt unverbindlich anfragen</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>

              {/* Right Column: 3D Cinematic Smartphone Device (5 Cols) */}
              <div className="lg:col-span-5 flex justify-center perspective-[1400px]">
                <div className="relative group transition-all duration-700 ease-out transform lg:rotate-y-[-10deg] lg:rotate-x-[6deg] hover:rotate-0">
                  {/* Outer Glow Halo */}
                  <div className="absolute -inset-4 rounded-[56px] bg-gradient-to-r from-[#E63946]/40 via-[#FF7A00]/30 to-[#D4AF37]/30 blur-2xl opacity-60 group-hover:opacity-100 transition duration-700 pointer-events-none" />

                  {/* 3D Mobile Phone Chassis (Titanium Bezel) */}
                  <div className="relative rounded-[48px] p-3.5 sm:p-4 bg-gradient-to-b from-[#333d52] via-[#1a202c] to-[#0b0e14] border-2 border-white/20 shadow-[0_30px_90px_rgba(0,0,0,0.95)] max-w-[340px] sm:max-w-[380px] w-full">
                    {/* Glossy Reflection Highlight */}
                    <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-white/15 to-transparent rounded-t-[44px] pointer-events-none" />

                    {/* Smartphone Screen Viewport */}
                    <div className="relative rounded-[36px] bg-[#07090F] p-4 text-white overflow-hidden border border-white/10 flex flex-col justify-between min-h-[540px]">
                      {/* Dynamic Island Notch */}
                      <div className="mx-auto w-24 h-5 rounded-full bg-black border border-white/10 flex items-center justify-between px-2.5 mb-2 shadow-inner">
                        <span className="h-2 w-2 rounded-full bg-[#111] border border-white/10" />
                        <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />
                      </div>

                      {/* Top Status Bar */}
                      <div className="flex items-center justify-between text-[10px] font-mono text-slate-300 pb-3 border-b border-white/10">
                        <span className="font-bold">09:41</span>
                        <div className="flex items-center gap-1.5">
                          <span>5G 🇨🇭</span>
                          <Wifi size={11} />
                          <BatteryCharging size={13} className="text-emerald-400" />
                        </div>
                      </div>

                      {/* Prominent RedWORK App Header */}
                      <div className="py-3 flex items-center justify-between">
                        <div>
                          <div className="text-xl font-black tracking-wider text-white">
                            Red<span className="text-[#E63946]">WORK</span>
                          </div>
                          <div className="text-[10px] font-mono text-slate-400">Swiss Cloud & Hosting App</div>
                        </div>
                        <span className="text-[9px] font-mono font-black uppercase px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-ping" />
                          ONLINE
                        </span>
                      </div>

                      {/* Interactive Push Notification Banner Simulation */}
                      <div className="p-3 rounded-2xl bg-gradient-to-r from-[#182033] to-[#121826] border border-cyan-500/40 shadow-lg text-left my-2 animate-pulse">
                        <div className="flex items-center gap-2 mb-1">
                          <Bell size={13} className="text-cyan-400" />
                          <span className="text-[10px] font-mono font-bold text-cyan-300 uppercase tracking-wider">
                            Push-Mitteilung (Neu)
                          </span>
                        </div>
                        <p className="text-xs font-bold text-white leading-tight">
                          Ihr Webauftritt & NVMe-Hosting jetzt live als iOS & Android App! 🚀
                        </p>
                        <span className="text-[9px] text-slate-400 block mt-1 font-mono">vor 2 Minuten • RedWORK Cloud</span>
                      </div>

                      {/* Webview Simulated Content Grid */}
                      <div className="space-y-2 py-2">
                        <div className="p-2.5 rounded-xl bg-white/[0.04] border border-white/5 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full bg-emerald-400" />
                            <span className="text-xs font-bold text-slate-200">NVMe High-Speed Webspace</span>
                          </div>
                          <span className="text-[10px] font-mono text-emerald-400 font-bold">10 Gbit/s</span>
                        </div>

                        <div className="p-2.5 rounded-xl bg-white/[0.04] border border-white/5 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full bg-[#E63946]" />
                            <span className="text-xs font-bold text-slate-200">Zürich Tier-IV Datacenter</span>
                          </div>
                          <span className="text-[10px] font-mono text-slate-300">DSG Konform</span>
                        </div>

                        <div className="p-2.5 rounded-xl bg-white/[0.04] border border-white/5 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full bg-[#D4AF37]" />
                            <span className="text-xs font-bold text-slate-200">Schweizer Support-Desk</span>
                          </div>
                          <span className="text-[10px] font-mono text-amber-300 font-bold">24/7 Live</span>
                        </div>
                      </div>

                      {/* App Store & Google Play Badges inside screen footer */}
                      <div className="pt-3 border-t border-white/10 flex items-center justify-between text-[10px] font-mono text-slate-400">
                        <div className="flex items-center gap-1.5">
                          <span className="px-2 py-1 rounded-lg bg-white/10 text-white font-bold text-[9px] border border-white/10">
                            🍎 App Store
                          </span>
                          <span className="px-2 py-1 rounded-lg bg-white/10 text-white font-bold text-[9px] border border-white/10">
                            🤖 Google Play
                          </span>
                        </div>
                        <span className="font-mono text-emerald-400 font-bold">v2.4 Ready</span>
                      </div>

                      {/* Home Indicator Bar */}
                      <div className="mx-auto w-28 h-1 rounded-full bg-white/30 mt-3" />
                    </div>

                    {/* Floating 3D Badge on side */}
                    <div className="absolute -bottom-4 -left-4 bg-gradient-to-r from-[#E63946] to-[#FF7A00] text-white p-2 px-3 rounded-2xl shadow-xl border border-white/20 text-xs font-mono font-black flex items-center gap-1.5">
                      <Sparkles size={13} />
                      <span>CHF 299.- FESTPREIS</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================================================== */}
        {/* 2. LIVE DOMAIN CHECKER BAR (Jrodix Style)          */}
        {/* ================================================== */}
        <section id="domain-checker" className="py-12 border-b border-white/10 bg-[#090C14]">
          <div className="mx-auto max-w-4xl px-4 sm:px-6">
            <div className="text-center mb-6">
              <span className="text-xs font-black uppercase tracking-widest text-[#D4AF37]">
                Live Domain-Abfrage
              </span>
              <h2 className="text-2xl sm:text-3xl font-black text-white mt-1">
                Finden Sie jetzt Ihre perfekte Schweizer Wunschdomain
              </h2>
              <p className="text-xs sm:text-sm text-slate-400 mt-1">
                Prüfen Sie in Echtzeit die Verfügbarkeit für .ch, .com, .de, .net oder andere Endungen.
              </p>
            </div>

            {/* Interactive Search Bar */}
            <form onSubmit={handleDomainCheck} className="relative flex flex-col sm:flex-row gap-2 bg-[#0F1420] p-2.5 rounded-2xl border border-white/15 shadow-xl">
              <div className="relative grow flex items-center min-w-0">
                <Globe size={18} className="absolute left-3.5 text-slate-400 shrink-0" />
                <input
                  type="text"
                  value={domainSearchInput}
                  onChange={(e) => setDomainSearchInput(e.target.value)}
                  placeholder="Ihre-Wunschdomain (z.B. meinunternehmen)"
                  className="w-full pl-10 pr-3 py-3 rounded-xl bg-transparent text-xs sm:text-sm text-white focus:outline-none placeholder:text-slate-500 font-medium"
                />
              </div>

              {/* TLD Quick Select */}
              <select
                value={selectedTld}
                onChange={(e) => setSelectedTld(e.target.value)}
                className="w-full sm:w-auto px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-xs font-bold text-slate-200 focus:outline-none"
              >
                <option value="ch" className="bg-[#0f172a]">.ch (CHF 15.00/Jahr)</option>
                <option value="com" className="bg-[#0f172a]">.com (CHF 12.00/Jahr)</option>
                <option value="de" className="bg-[#0f172a]">.de (CHF 8.00/Jahr)</option>
                <option value="net" className="bg-[#0f172a]">.net (CHF 16.00/Jahr)</option>
                <option value="org" className="bg-[#0f172a]">.org (CHF 17.00/Jahr)</option>
                <option value="li" className="bg-[#0f172a]">.li (CHF 19.00/Jahr)</option>
                <option value="io" className="bg-[#0f172a]">.io (CHF 45.00/Jahr)</option>
              </select>

              <button
                type="submit"
                disabled={domainSearching || !domainSearchInput.trim()}
                className="w-full sm:w-auto px-6 py-3 rounded-xl bg-[#E63946] hover:bg-[#c5303d] disabled:opacity-50 text-white font-bold text-xs sm:text-sm shadow-md transition flex items-center justify-center gap-2 shrink-0 cursor-pointer"
              >
                {domainSearching ? (
                  <>
                    <RefreshCw size={15} className="animate-spin" />
                    <span>Prüfe...</span>
                  </>
                ) : (
                  <>
                    <Search size={15} />
                    <span>Prüfen</span>
                  </>
                )}
              </button>
            </form>

            {/* Live Domain Result Display */}
            {domainSearchResult && (
              <div
                className={`mt-4 p-4 rounded-2xl border transition animate-fadeIn ${
                  domainSearchResult.available
                    ? "bg-emerald-950/30 border-emerald-500/40"
                    : "bg-amber-950/30 border-amber-500/40"
                }`}
              >
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    {domainSearchResult.available ? (
                      <CheckCircle2 size={24} className="text-emerald-400 shrink-0" />
                    ) : (
                      <AlertCircle size={24} className="text-amber-400 shrink-0" />
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-base font-black text-white">
                          {domainSearchResult.domain}
                        </span>
                        <span
                          className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${
                            domainSearchResult.available
                              ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                              : "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                          }`}
                        >
                          {domainSearchResult.available ? "VERFÜGBAR" : "VERGEBEN"}
                        </span>
                      </div>
                      <p className="text-xs text-slate-300 mt-0.5">
                        {domainSearchResult.message}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="font-mono font-bold text-sm text-white">
                      CHF {Number(domainSearchResult.price || 15).toFixed(2)} / Jahr
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        openOrderModal(activePlans[0] || mainWebhostingPlans[0], domainSearchResult.domain)
                      }
                      className="px-4 py-2 rounded-xl bg-white text-slate-900 hover:bg-slate-100 font-bold text-xs shadow-md transition flex items-center gap-1.5"
                    >
                      <ShoppingCart size={13} />
                      <span>Mit Hosting bestellen</span>
                    </button>
                  </div>
                </div>

                {/* Alternatives */}
                {domainSearchResult.suggestions?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-bold text-slate-400">Alternative Endungen:</span>
                    {domainSearchResult.suggestions.map((s, idx) => (
                      <span
                        key={idx}
                        className="text-[11px] px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-slate-300 flex items-center gap-1 font-mono"
                      >
                        <span>{s.domain}</span>
                        <span className="text-slate-400 font-sans">
                          (CHF {Number(s.price).toFixed(2)})
                        </span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* ================================================== */}
        {/* 3. 3D CINEMATIC SERVER RACK SHOWCASE (7 Pillars)   */}
        {/* ================================================== */}
        <section id="kategorien" className="py-20 lg:py-28 border-b border-white/10 bg-gradient-to-b from-[#090C14] via-[#0D1220] to-[#080B12] relative overflow-hidden">
          {/* Subtle Cyber Grid Lines & Ambient Lighting */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(230,57,70,0.15),transparent)] pointer-events-none" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="text-xs font-black uppercase tracking-widest text-[#E63946] bg-[#E63946]/10 px-4 py-1.5 rounded-full border border-[#E63946]/25 inline-flex items-center gap-2 mb-4 shadow-sm">
                <span className="h-2 w-2 rounded-full bg-[#E63946] animate-pulse" />
                🇨🇭 3D Server-Rack Architektur • Schweizer Hochleistungs-Cluster
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight">
                Interaktives 3D Server-Chassis
              </h2>
              <p className="text-sm sm:text-base text-slate-300 mt-3 max-w-2xl mx-auto leading-relaxed">
                Erkunden Sie unsere 7 Schweizer Rechenzentrums-Knotenpunkte. Wechseln Sie interaktiv zwischen den Blade-Modulen, um Live-Telemetrie und konfigurierbare Tarife abzurufen:
              </p>
            </div>

            {/* 3D Cinematic Server Slider Component */}
            <CinematicServerSlider
              activeCategory={activeCategory}
              onSelectCategory={handleSelectCategory}
              onScrollToPackages={() => {
                const el = document.getElementById("produkte");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
            />
          </div>
        </section>

        {/* ================================================== */}
        {/* 4. DYNAMIC PRICING CARDS BY CATEGORY               */}
        {/* ================================================== */}
        <section id="produkte" className="py-20 lg:py-24 border-b border-white/10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {/* Category Quick Switcher Tabs (100% Mobile & Tablet Responsive) */}
            <div className="mb-10 sm:mb-12">
              <div className="text-center mb-4 sm:hidden">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                  Kategorie wechseln:
                </span>
              </div>
              <div className="flex items-center justify-start sm:justify-center gap-2 overflow-x-auto no-scrollbar pb-3 px-1">
                {HOSTING_CATEGORIES.map((cat) => {
                  const CatIcon = cat.icon;
                  const isSelected = cat.id === activeCategory;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => handleSelectCategory(cat.id)}
                      className={`flex items-center gap-2 px-3.5 py-2.5 rounded-2xl text-xs font-bold transition-all shrink-0 cursor-pointer border ${
                        isSelected
                          ? "bg-gradient-to-r from-[#E63946] to-[#FF7A00] text-white border-[#E63946] shadow-lg shadow-red-600/30 scale-105"
                          : "bg-[#0C101A] text-slate-300 border-white/10 hover:border-white/25 hover:text-white hover:bg-white/5"
                      }`}
                    >
                      <CatIcon size={14} className={isSelected ? "text-white" : "text-[#D4AF37]"} />
                      <span>{cat.label}</span>
                      {cat.badge && (
                        <span
                          className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded-md border hidden md:inline-block ${
                            isSelected
                              ? "bg-white/20 text-white border-white/30"
                              : "bg-white/5 text-slate-400 border-white/10"
                          }`}
                        >
                          {cat.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* If Domains Category is chosen, show Domain Table directly */}
            {activeCategory === "domains" ? (
              <div>
                <div className="text-center max-w-3xl mx-auto mb-10">
                  <span className="text-xs font-black uppercase tracking-widest text-[#D4AF37]">
                    Domain-Preise & TLDs
                  </span>
                  <h3 className="text-2xl sm:text-3xl font-black text-white mt-2">
                    Transparente Domain-Registrierungen & Transfers
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-400 mt-2">
                    Alle Domains beinhalten kostenlose DNS-Verwaltung, Whois-Privacy und Weiterleitungen.
                  </p>
                </div>

                {/* Desktop / Tablet Table View (hidden on mobile < 768px) */}
                <div className="hidden md:block overflow-x-auto rounded-3xl border border-white/10 bg-[#0C101A] shadow-2xl">
                  <table className="w-full text-left border-collapse min-w-[650px]">
                    <thead>
                      <tr className="border-b border-white/10 bg-white/[0.03] text-xs font-black text-slate-300 uppercase tracking-wider">
                        <th className="p-4 sm:p-5">Endung (TLD)</th>
                        <th className="p-4 sm:p-5">Kategorie / Zweck</th>
                        <th className="p-4 sm:p-5 text-center">Neuregistrierung</th>
                        <th className="p-4 sm:p-5 text-center">Verlängerung</th>
                        <th className="p-4 sm:p-5 text-center">Transfer</th>
                        <th className="p-4 sm:p-5 text-right">Aktion</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5 text-xs sm:text-sm">
                      {TLD_PRICING_TABLE.map((tld, idx) => (
                        <tr key={idx} className="hover:bg-white/[0.02] transition">
                          <td className="p-4 sm:p-5 font-mono text-base font-black text-white">
                            {tld.tld}
                          </td>
                          <td className="p-4 sm:p-5">
                            <span className="rounded-full bg-white/5 px-2.5 py-1 text-[11px] font-bold text-slate-300 border border-white/10">
                              {tld.badge}
                            </span>
                          </td>
                          <td className="p-4 sm:p-5 text-center font-mono font-bold text-emerald-400">
                            CHF {tld.reg.toFixed(2)} / Jahr
                          </td>
                          <td className="p-4 sm:p-5 text-center font-mono text-slate-300">
                            CHF {tld.renew.toFixed(2)} / Jahr
                          </td>
                          <td className="p-4 sm:p-5 text-center font-mono text-slate-300">
                            CHF {tld.transfer.toFixed(2)}
                          </td>
                          <td className="p-4 sm:p-5 text-right">
                            <button
                              type="button"
                              onClick={() => {
                                setDomainSearchInput(`ihredomain${tld.tld}`);
                                setSelectedTld(tld.tld.replace(".", ""));
                                const el = document.getElementById("domain-checker");
                                if (el) el.scrollIntoView({ behavior: "smooth" });
                              }}
                              className="px-4 py-2 rounded-xl bg-[#E63946] hover:bg-[#c5303d] text-white font-bold text-xs transition cursor-pointer"
                            >
                              Prüfen
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards View (shown only on mobile < 768px) */}
                <div className="block md:hidden space-y-3">
                  {TLD_PRICING_TABLE.map((tld, idx) => (
                    <div
                      key={idx}
                      className="rounded-2xl border border-white/10 bg-[#0C101A] p-4 shadow-lg flex flex-col gap-3"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-2xl font-black text-white">
                            {tld.tld}
                          </span>
                          <span className="rounded-full bg-white/5 px-2.5 py-0.5 text-[10px] font-bold text-slate-300 border border-white/10">
                            {tld.badge}
                          </span>
                        </div>
                        <span className="font-mono text-sm font-black text-emerald-400">
                          CHF {tld.reg.toFixed(2)} / J.
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-[11px] font-mono bg-white/[0.03] p-2.5 rounded-xl border border-white/5">
                        <div>
                          <span className="text-slate-500 block text-[9px] uppercase font-sans">Verlängerung:</span>
                          <span className="text-slate-200 font-bold">CHF {tld.renew.toFixed(2)} / Jahr</span>
                        </div>
                        <div>
                          <span className="text-slate-500 block text-[9px] uppercase font-sans">Transfer:</span>
                          <span className="text-slate-200 font-bold">CHF {tld.transfer.toFixed(2)}</span>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setDomainSearchInput(`ihredomain${tld.tld}`);
                          setSelectedTld(tld.tld.replace(".", ""));
                          const el = document.getElementById("domain-checker");
                          if (el) el.scrollIntoView({ behavior: "smooth" });
                        }}
                        className="w-full py-2.5 px-4 rounded-xl bg-[#E63946] hover:bg-[#c5303d] text-white font-bold text-xs flex items-center justify-center gap-2 transition cursor-pointer shadow-md"
                      >
                        <Search size={14} />
                        <span>Domain {tld.tld} jetzt prüfen & registrieren</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : activeCategory === "vps" ? (
              <MeoHostVdsView
                plans={activePlans}
                openOrderModal={openOrderModal}
                billingCycle={billingCycle}
                setBillingCycle={setBillingCycle}
              />
            ) : (
              <div>
                {/* Billing Toggle (Monatlich / Jährlich) - Fully Responsive */}
                <div className="flex justify-center mb-8 sm:mb-10 px-2">
                  <div className="w-full sm:w-auto inline-flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-[#0F1420] p-1.5 rounded-2xl border border-white/15 shadow-inner">
                    <button
                      type="button"
                      onClick={() => setBillingCycle("monthly")}
                      className={`w-full sm:w-auto px-5 py-2.5 rounded-xl text-xs font-bold transition text-center cursor-pointer ${
                        billingCycle === "monthly"
                          ? "bg-[#E63946] text-white shadow-md"
                          : "text-slate-400 hover:text-white"
                      }`}
                    >
                      Monatliche Abrechnung
                    </button>
                    <button
                      type="button"
                      onClick={() => setBillingCycle("yearly")}
                      className={`w-full sm:w-auto px-5 py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 cursor-pointer ${
                        billingCycle === "yearly"
                          ? "bg-[#E63946] text-white shadow-md"
                          : "text-slate-400 hover:text-white"
                      }`}
                    >
                      <span>Jährliche Abrechnung</span>
                      <span className="rounded-full bg-emerald-500/20 text-emerald-400 text-[10px] font-black px-2 py-0.5 border border-emerald-500/30 shrink-0">
                        10% RABATT 🔥
                      </span>
                    </button>
                  </div>
                </div>

                {/* Cards Grid - 100% Responsive on Web, Tablet & Mobile */}
                <div className={`grid grid-cols-1 md:grid-cols-2 ${activePlans.length >= 4 ? "xl:grid-cols-4" : activePlans.length === 3 ? "xl:grid-cols-3 max-w-6xl mx-auto" : "xl:grid-cols-2 max-w-4xl mx-auto"} gap-4 sm:gap-6 items-stretch`}>
                  {activePlans.map((plan, idx) => {
                    const isHighlight = plan.isHighlight;
                    const monthlyPrice = Number(plan.unitPrice);
                    const isYearly = billingCycle === "yearly";
                    const isAnnual = isAnnualOnlyProduct(plan);
                    const effectiveMonthly = isAnnual ? monthlyPrice : isYearly ? monthlyPrice * 0.9 : monthlyPrice;
                    const yearlyTotal = isAnnual ? monthlyPrice : monthlyPrice * 12 * 0.9;

                    return (
                      <div
                        key={plan.id || idx}
                        className={`relative flex flex-col justify-between rounded-3xl p-5 sm:p-7 transition-all duration-300 hover:-translate-y-1.5 min-w-0 ${
                          isHighlight
                            ? "bg-gradient-to-b from-[#161e33] via-[#0F1420] to-[#0A0D16] border-2 border-[#E63946] shadow-[0_20px_50px_rgba(230,57,70,0.2)]"
                            : "bg-gradient-to-b from-[#0F1420] to-[#0A0D16] border border-white/10 shadow-xl hover:border-white/25"
                        }`}
                      >
                        {/* Badge */}
                        {plan.badge && (
                          <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap z-10">
                            <span
                              className={`rounded-full px-3.5 py-1 text-[10px] font-black uppercase tracking-wider shadow-md ${
                                isHighlight
                                  ? "bg-gradient-to-r from-[#E63946] to-[#FF7A00] text-white"
                                  : "bg-slate-800 text-slate-200 border border-slate-700"
                              }`}
                            >
                              {plan.badge}
                            </span>
                          </div>
                        )}

                        <div>
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <h3 className="text-lg sm:text-xl font-black text-white break-words min-w-0">{plan.displayName}</h3>
                            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/5 text-[#D4AF37] border border-white/10 shrink-0">
                              <Server className="h-4 w-4" />
                            </div>
                          </div>

                          <p className="text-xs text-slate-400 min-h-[44px] leading-relaxed mb-5 break-words">
                            {plan.shortDescription}
                          </p>

                          {/* Quick Specs Pills if available */}
                          {plan.specs && (
                            <div className="grid grid-cols-2 gap-2 mb-5 pb-5 border-b border-white/10 text-[11px] font-mono text-slate-300">
                              <div className="bg-white/[0.03] p-2 rounded-xl border border-white/5 min-w-0">
                                <span className="text-slate-500 block text-[9px] uppercase font-sans">Speicher</span>
                                <span className="font-bold text-white truncate block">{plan.specs.disk}</span>
                              </div>
                              <div className="bg-white/[0.03] p-2 rounded-xl border border-white/5 min-w-0">
                                <span className="text-slate-500 block text-[9px] uppercase font-sans">Ressourcen</span>
                                <span className="font-bold text-[#D4AF37] truncate block">{plan.specs.ram}</span>
                              </div>
                            </div>
                          )}

                          {/* Price Block */}
                          <div className="rounded-2xl bg-white/[0.04] p-4 border border-white/5 mb-6">
                            <div className="flex items-baseline gap-1.5 flex-wrap">
                              <span className="text-xs font-bold text-slate-400">CHF</span>
                              <span className="text-2xl sm:text-3xl font-black text-white tracking-tight font-mono">
                                {effectiveMonthly.toFixed(2)}
                              </span>
                              <span className="text-xs font-semibold text-slate-400">
                                {isAnnual || activeCategory === "ssl" || activeCategory === "domains" ? "/ Jahr" : "/ Mt."}
                              </span>
                            </div>
                            {isYearly && !isAnnual && activeCategory !== "ssl" ? (
                              <div className="text-[11px] text-emerald-400 font-bold mt-1">
                                CHF {yearlyTotal.toFixed(2)} / Jahr (Sie sparen 10%)
                              </div>
                            ) : isAnnual ? (
                              <div className="text-[11px] text-emerald-400 font-bold mt-1">
                                CHF {yearlyTotal.toFixed(2)} / Jahr
                              </div>
                            ) : (
                              <div className="text-[11px] text-slate-500 mt-1">
                                Keine Einrichtungsgebühr, inkl. MwSt
                              </div>
                            )}
                          </div>

                          {/* Features Checklist */}
                          <div className="space-y-2.5 mb-7">
                            <p className="text-[11px] font-black uppercase tracking-wider text-slate-400">
                              Enthaltene Leistungen:
                            </p>
                            {plan.features.map((feat, fIdx) => (
                              <div key={fIdx} className="flex items-start gap-2 text-xs text-slate-300 min-w-0">
                                <CheckCircle2
                                  className={`h-4 w-4 shrink-0 mt-0.5 ${
                                    isHighlight ? "text-[#E63946]" : "text-[#D4AF37]"
                                  }`}
                                />
                                <span className="break-words min-w-0 leading-relaxed">{feat}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Order Action Button */}
                        <div className="pt-4 border-t border-white/10">
                          <button
                            type="button"
                            onClick={() => openOrderModal(plan)}
                            className={`w-full py-3 px-4 rounded-xl font-black text-xs sm:text-sm flex items-center justify-center gap-2 transition shadow-lg cursor-pointer ${
                              isHighlight
                                ? "bg-[#E63946] hover:bg-[#c5303d] text-white shadow-red-600/25"
                                : "bg-white text-slate-900 hover:bg-slate-100"
                            }`}
                          >
                            <ShoppingCart size={15} />
                            <span>Jetzt bestellen</span>
                            <ArrowRight size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ================================================== */}
        {/* 5. SCHWEIZER ENTERPRISE-INFRASTRUKTUR & TECHNOLOGIE */}
        {/* ================================================== */}
        <section className="py-20 lg:py-24 border-b border-white/10 bg-[#090C14]">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-xs font-black uppercase tracking-widest text-[#D4AF37]">
                Enterprise Standards • Schweizer Rechenzentrum & Cloud-Infrastruktur
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-white mt-2">
                Warum RedWORK die erste Wahl für Schweizer Hosting ist
              </h2>
              <p className="text-sm sm:text-base text-slate-400 mt-2">
                Modernste Serverarchitektur, kompromisslose Sicherheit und echte Schweizer Betreuung.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Feature 1 */}
              <div className="bg-[#0F1420] p-7 rounded-3xl border border-white/10 hover:border-[#E63946]/40 transition group">
                <div className="h-12 w-12 rounded-2xl bg-[#E63946]/10 border border-[#E63946]/20 flex items-center justify-center text-[#E63946] mb-5 group-hover:scale-110 transition">
                  <Zap size={24} />
                </div>
                <h3 className="text-xl font-black text-white mb-2">LiteSpeed Web Server</h3>
                <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                  Bis zu 84x schnellere WordPress-Ladezeiten als Apache und 5x schneller als Nginx. Integriertes LSCache, ESI und modernstes HTTP/3 Protokoll.
                </p>
              </div>

              {/* Feature 2 */}
              <div className="bg-[#0F1420] p-7 rounded-3xl border border-white/10 hover:border-[#D4AF37]/40 transition group">
                <div className="h-12 w-12 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] mb-5 group-hover:scale-110 transition">
                  <HardDrive size={24} />
                </div>
                <h3 className="text-xl font-black text-white mb-2">Samsung Gen4 NVMe RAID-10</h3>
                <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                  Enterprise PCIe 4.0 NVMe SSDs mit Lesegeschwindigkeiten von über 7'000 MB/s. Null I/O-Wartezeiten für anspruchsvolle Datenbanken.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="bg-[#0F1420] p-7 rounded-3xl border border-white/10 hover:border-emerald-500/40 transition group">
                <div className="h-12 w-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 mb-5 group-hover:scale-110 transition">
                  <ShieldCheck size={24} />
                </div>
                <h3 className="text-xl font-black text-white mb-2">Imunify360 & CageFS</h3>
                <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                  CloudLinux CageFS isoliert jeden Account zu 100%. Imunify360 erkennt und neutralisiert Malware, Brute-Force und Zero-Day Exploits vollautomatisch.
                </p>
              </div>

              {/* Feature 4 */}
              <div className="bg-[#0F1420] p-7 rounded-3xl border border-white/10 hover:border-blue-500/40 transition group">
                <div className="h-12 w-12 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 mb-5 group-hover:scale-110 transition">
                  <RefreshCw size={24} />
                </div>
                <h3 className="text-xl font-black text-white mb-2">Tägliche Backups (JetBackup)</h3>
                <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                  Ihre Daten werden täglich vollautomatisch gesichert und bis zu 30 Tage aufbewahrt. Stellen Sie Dateien, Datenbanken oder E-Mails mit 1 Klick wieder her.
                </p>
              </div>

              {/* Feature 5 */}
              <div className="bg-[#0F1420] p-7 rounded-3xl border border-white/10 hover:border-amber-500/40 transition group">
                <div className="h-12 w-12 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 mb-5 group-hover:scale-110 transition">
                  <Sliders size={24} />
                </div>
                <h3 className="text-xl font-black text-white mb-2">Multi-PHP & cPanel Dashboard</h3>
                <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                  Wählen Sie pro Domain flexibel zwischen PHP 7.4, 8.1, 8.2 und 8.3. Nutzen Sie Softaculous mit über 400 1-Klick Anwendungen wie WordPress und PrestaShop.
                </p>
              </div>

              {/* Feature 6 */}
              <div className="bg-[#0F1420] p-7 rounded-3xl border border-white/10 hover:border-[#E63946]/40 transition group">
                <div className="h-12 w-12 rounded-2xl bg-[#E63946]/10 border border-[#E63946]/20 flex items-center justify-center text-[#E63946] mb-5 group-hover:scale-110 transition">
                  <Building2 size={24} />
                </div>
                <h3 className="text-xl font-black text-white mb-2">100% Serverstandort Schweiz</h3>
                <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                  Tier-3+ Rechenzentren in Zürich und Basel. 100% DSG-Konformität, FINMA-konform und betrieben mit 100% zertifiziertem Schweizer Ökostrom.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ================================================== */}
        {/* 6. DETAILLIERTER PAKET-VERGLEICH (Jrodix Matrix)   */}
        {/* ================================================== */}
        <section id="vergleich" className="py-20 lg:py-28 border-b border-white/10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <span className="text-xs font-black uppercase tracking-widest text-[#D4AF37]">
                Detaillierte Matrix
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-white mt-2">
                Alle technischen Spezifikationen im Direktvergleich
              </h2>
              <p className="text-sm text-slate-400 mt-2">
                Transparente Leistungsmerkmale, Speicherressourcen und Schweizer SLA-Standards.
              </p>
            </div>

            {/* Comparison Table Container with Sticky First Column & Swipe Hint */}
            <div className="overflow-x-auto rounded-3xl border border-white/10 bg-[#0C101A] shadow-2xl relative">
              <div className="flex items-center justify-between px-4 py-2.5 bg-white/[0.04] border-b border-white/10 text-[11px] text-slate-300 font-mono xl:hidden">
                <span>← Tabelle seitlich wischen zum Vergleichen →</span>
                <span className="text-[#D4AF37] font-bold">4 NVMe Tarife</span>
              </div>
              <table className="w-full text-left border-collapse min-w-[760px]">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.03]">
                    <th className="p-4 sm:p-5 text-xs sm:text-sm font-black text-white w-1/3 sticky left-0 bg-[#0C101A] z-20 shadow-[4px_0_12px_rgba(0,0,0,0.5)]">
                      Hosting-Merkmal
                    </th>
                    {mainWebhostingPlans.map((plan, pIdx) => (
                      <th
                        key={pIdx}
                        className={`p-4 sm:p-5 text-center ${
                          plan.isHighlight ? "bg-[#E63946]/10 text-[#E63946]" : "text-white"
                        }`}
                        style={{ width: `${66 / mainWebhostingPlans.length}%` }}
                      >
                        <div className="font-extrabold text-xs sm:text-sm">{plan.displayName}</div>
                        <div className="text-xs font-mono font-bold text-slate-400 mt-0.5">
                          CHF {Number(plan.unitPrice).toFixed(2)}/Mt.
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_SECTIONS.map((section, sIdx) => (
                    <React.Fragment key={sIdx}>
                      {/* Category Header Row */}
                      <tr className="bg-white/[0.04] border-t border-b border-white/10">
                        <td
                          colSpan={mainWebhostingPlans.length + 1}
                          className="px-4 sm:px-5 py-3 text-xs font-black uppercase tracking-wider text-[#D4AF37] sticky left-0 bg-[#121826] z-10 shadow-[4px_0_12px_rgba(0,0,0,0.5)]"
                        >
                          {section.category}
                        </td>
                      </tr>

                      {/* Detail Rows */}
                      {section.rows.map((row, rIdx) => (
                        <tr
                          key={rIdx}
                          className="border-b border-white/5 hover:bg-white/[0.02] transition text-xs sm:text-sm"
                        >
                          <td className="p-3.5 sm:p-4 md:p-5 font-semibold text-slate-300 sticky left-0 bg-[#0C101A] z-10 shadow-[4px_0_12px_rgba(0,0,0,0.5)]">
                            {row.label}
                          </td>
                          {row.values.map((val, vIdx) => {
                            const isHigh = mainWebhostingPlans[vIdx]?.isHighlight;
                            return (
                              <td
                                key={vIdx}
                                className={`p-3.5 sm:p-4 md:p-5 text-center ${
                                  isHigh ? "bg-[#E63946]/5 font-bold text-white" : "text-slate-300"
                                }`}
                              >
                                {typeof val === "boolean" ? (
                                  val ? (
                                    <Check className="h-4 w-4 mx-auto text-emerald-400" />
                                  ) : (
                                    <X className="h-4 w-4 mx-auto text-slate-600" />
                                  )
                                ) : (
                                  <span>{val}</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}

                  {/* Table Footer Action Row */}
                  <tr className="bg-white/[0.02] border-t border-white/10">
                    <td className="p-4 sm:p-5 font-bold text-white sticky left-0 bg-[#0C101A] z-20 shadow-[4px_0_12px_rgba(0,0,0,0.5)]">
                      Sofort bestellen:
                    </td>
                    {mainWebhostingPlans.map((plan, pIdx) => (
                      <td key={pIdx} className="p-3 sm:p-4 text-center">
                        <button
                          type="button"
                          onClick={() => openOrderModal(plan)}
                          className={`w-full py-2.5 px-3 rounded-xl text-xs font-black transition ${
                            plan.isHighlight
                              ? "bg-[#E63946] text-white hover:bg-[#c5303d]"
                              : "bg-white/10 text-white hover:bg-white/20"
                          }`}
                        >
                          Auswählen
                        </button>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ================================================== */}
        {/* 7. SERVER INFRASTRUKTUR & SPEED (Hardware Showcase)*/}
        {/* ================================================== */}
        <section className="py-20 lg:py-28 border-b border-white/10 bg-[#090C14]/60">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-6 space-y-6">
                <span className="text-xs font-black uppercase tracking-widest text-[#E63946]">
                  Tier-3+ Infrastruktur
                </span>
                <h2 className="text-3xl sm:text-4xl font-black text-white leading-tight">
                  Schweizer Hochsicherheits-Rechenzentren in Zürich & Basel
                </h2>
                <p className="text-sm sm:text-base text-slate-400 leading-relaxed">
                  Ihre Websites laufen nicht auf überbuchten Billigservern im Ausland. Wir setzen
                  auf modernste AMD EPYC Prozessoren, Samsung Enterprise NVMe SSDs und eine
                  mehrfach redundante 40 Gbit/s Glasfaseranbindung direkt an den SwissIX.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pt-2">
                  <div className="bg-[#0F1420] p-4 rounded-2xl border border-white/10">
                    <Cpu className="text-[#D4AF37] mb-2" size={24} />
                    <div className="text-lg font-black text-white">PCIe 4.0 NVMe</div>
                    <p className="text-xs text-slate-400 mt-1">
                      Bis zu 7-fach schnellere Lese- und Schreibzeiten als Standard-SSDs.
                    </p>
                  </div>
                  <div className="bg-[#0F1420] p-4 rounded-2xl border border-white/10">
                    <Zap className="text-[#E63946] mb-2" size={24} />
                    <div className="text-lg font-black text-white">LiteSpeed Cache</div>
                    <p className="text-xs text-slate-400 mt-1">
                      Bis zu 84x schnellere WordPress-Ladezeiten dank integriertem LSCache.
                    </p>
                  </div>
                  <div className="bg-[#0F1420] p-4 rounded-2xl border border-white/10">
                    <ShieldCheck className="text-emerald-400 mb-2" size={24} />
                    <div className="text-lg font-black text-white">100% Ökostrom</div>
                    <p className="text-xs text-slate-400 mt-1">
                      Zertifizierte Schweizer Wasserkraft und modernste Energieeffizienz (PUE &lt; 1.15).
                    </p>
                  </div>
                  <div className="bg-[#0F1420] p-4 rounded-2xl border border-white/10">
                    <Lock className="text-blue-400 mb-2" size={24} />
                    <div className="text-lg font-black text-white">DDoS-Schutz</div>
                    <p className="text-xs text-slate-400 mt-1">
                      Multi-Layer Echtzeitfilterung gegen volumetrische und App-Angriffe.
                    </p>
                  </div>
                </div>
              </div>

              {/* Visual Hardware Specs Box */}
              <div className="lg:col-span-6 bg-gradient-to-br from-[#0F1420] to-[#07090F] p-4 sm:p-6 lg:p-8 rounded-3xl border border-white/15 shadow-2xl relative">
                <div className="flex items-center justify-between pb-4 border-b border-white/10">
                  <div className="flex items-center gap-2">
                    <Server size={18} className="text-[#D4AF37]" />
                    <span className="font-mono text-xs font-bold uppercase text-white">
                      Node: CH-ZH-01 (Production)
                    </span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2.5 py-0.5 rounded-full border border-emerald-500/20">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    LIVE UPTIME 99.98%
                  </span>
                </div>

                <div className="space-y-4 mt-6 font-mono text-xs text-slate-300">
                  <div>
                    <div className="flex justify-between mb-1 text-[11px]">
                      <span>NVMe SSD I/O Bandbreite</span>
                      <span className="text-[#D4AF37] font-bold">6'400 MB/s (Peak)</span>
                    </div>
                    <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-amber-400 to-[#E63946] w-[88%]" />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-1 text-[11px]">
                      <span>Netzwerk-Latenz (Schweiz & DACH)</span>
                      <span className="text-emerald-400 font-bold">&lt; 2.4 ms</span>
                    </div>
                    <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 w-[95%]" />
                    </div>
                  </div>

                  <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 mt-6 space-y-2">
                    <div className="text-[#D4AF37] font-bold">Hardware-Spezifikation:</div>
                    <div className="text-slate-400 text-[11px] leading-relaxed">
                      • CPU: Dual AMD EPYC 9654 (192 Cores / 384 Threads)<br />
                      • RAM: 768 GB DDR5 ECC Registered RAM<br />
                      • Storage: Enterprise NVMe U.3 RAID-10 gespiegelt<br />
                      • Uplink: 2x 25 Gbit/s redundante Interfaces
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================================================== */}
        {/* 8. KOSTENLOSER UMZUGSSERVICE ROADMAP (Jrodix)      */}
        {/* ================================================== */}
        <section className="py-20 border-b border-white/10 bg-gradient-to-r from-[#141B2D] via-[#0F1420] to-[#141B2D]">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <span className="text-[10px] font-black uppercase tracking-widest text-[#D4AF37] bg-[#D4AF37]/10 px-3 py-1 rounded-full border border-[#D4AF37]/20">
                100% Kostenloser Umzugsservice
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-white mt-3">
                Wechseln Sie in 4 einfachen Schritten zu RedWORK
              </h2>
              <p className="text-sm text-slate-300 mt-2">
                Unzufrieden mit Ladezeiten oder schlechtem Support bei Ihrem aktuellen Anbieter? Wir erledigen den kompletten Umzug für Sie.
              </p>
            </div>

            {/* 4 Steps Roadmap */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-[#0C101A] p-6 rounded-3xl border border-white/10 relative">
                <div className="text-4xl font-black text-white/10 mb-2 font-mono">01</div>
                <h4 className="text-base font-black text-white mb-2">Paket auswählen</h4>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Bestellen Sie das gewünschte Schweizer Hosting-Paket online mit 30 Tagen Geld-zurück-Garantie.
                </p>
              </div>

              <div className="bg-[#0C101A] p-6 rounded-3xl border border-white/10 relative">
                <div className="text-4xl font-black text-white/10 mb-2 font-mono">02</div>
                <h4 className="text-base font-black text-white mb-2">Zugangsdaten senden</h4>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Übermitteln Sie uns einfach Ihre cPanel-, Plesk-, FTP- oder WordPress-Daten Ihres bisherigen Providers.
                </p>
              </div>

              <div className="bg-[#0C101A] p-6 rounded-3xl border border-white/10 relative">
                <div className="text-4xl font-black text-[#E63946]/30 mb-2 font-mono">03</div>
                <h4 className="text-base font-black text-white mb-2">Technischer Transfer</h4>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Unsere Schweizer Ingenieure übertragen alle Dateien, MySQL-Datenbanken und E-Mail-Postfächer 1:1.
                </p>
              </div>

              <div className="bg-[#0C101A] p-6 rounded-3xl border border-white/10 relative">
                <div className="text-4xl font-black text-emerald-500/30 mb-2 font-mono">04</div>
                <h4 className="text-base font-black text-white mb-2">DNS-Umschaltung</h4>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Nach erfolgreichem Test schalten wir die Domain unterbrechungsfrei auf unsere High-Speed Server auf.
                </p>
              </div>
            </div>

            <div className="text-center mt-12">
              <button
                type="button"
                onClick={() => openOrderModal(mainWebhostingPlans[1])}
                className="w-full sm:w-auto px-6 sm:px-8 py-3.5 sm:py-4 rounded-2xl bg-[#E63946] hover:bg-[#c5303d] text-white font-black text-xs sm:text-sm shadow-xl shadow-red-600/30 transition transform hover:-translate-y-0.5 inline-flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>Jetzt kostenlosen Umzug anfordern</span>
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </section>

        {/* ================================================== */}
        {/* 9. FAQS ACCORDION SECTION (Erweitert)              */}
        {/* ================================================== */}
        <section className="py-20 lg:py-24 border-b border-white/10">
          <div className="mx-auto max-w-4xl px-4 sm:px-6">
            <div className="text-center mb-12">
              <span className="text-xs font-black uppercase tracking-widest text-[#D4AF37]">
                Häufige Fragen
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-white mt-1">
                Alles Wissenswerte zu Ihrem Webhosting & Server
              </h2>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, idx) => {
                const isOpen = activeFaq === idx;
                return (
                  <div
                    key={idx}
                    className="rounded-2xl border border-white/10 bg-[#0F1420] overflow-hidden transition"
                  >
                    <button
                      type="button"
                      onClick={() => setActiveFaq(isOpen ? null : idx)}
                      className="w-full p-5 text-left flex items-center justify-between gap-4 font-bold text-sm sm:text-base text-white hover:text-[#D4AF37] transition"
                    >
                      <span>{faq.q}</span>
                      <ChevronDown
                        size={18}
                        className={`shrink-0 transition-transform duration-200 ${
                          isOpen ? "rotate-180 text-[#D4AF37]" : "text-slate-400"
                        }`}
                      />
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5 text-xs sm:text-sm text-slate-300 leading-relaxed border-t border-white/5 pt-3">
                        {faq.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ================================================== */}
        {/* 10. FINAL CONVERSION CTA                           */}
        {/* ================================================== */}
        <section className="py-20 text-center">
          <div className="mx-auto max-w-4xl px-4 sm:px-6">
            <h2 className="text-3xl sm:text-5xl font-black text-white tracking-tight">
              Bereit für echtes Schweizer High-Speed Hosting?
            </h2>
            <p className="text-sm sm:text-base text-slate-400 mt-4 max-w-2xl mx-auto">
              Starten Sie noch heute. Ihre Bestellung wird in wenigen Minuten vollautomatisch
              bereitgestellt – inklusive 30 Tage Geld-zurück-Garantie und 100% Schweizer Datenschutz.
            </p>
            <div className="mt-8 flex items-center justify-center gap-4 flex-wrap">
              <a
                href="#produkte"
                className="px-8 py-4 rounded-2xl bg-[#E63946] hover:bg-[#c5303d] text-white font-black text-sm shadow-xl shadow-red-600/30 transition transform hover:-translate-y-0.5 inline-flex items-center gap-2"
              >
                <span>Paket auswählen & loslegen</span>
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* ================================================== */}
      {/* 11. INTEGRATED CHECKOUT MODAL (All Categories)     */}
      {/* ================================================== */}
      {showCheckoutModal && selectedProduct && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-2 sm:p-4 backdrop-blur-md"
          onClick={() => setShowCheckoutModal(false)}
        >
          <div
            className="bg-[#0C101A] border border-white/15 rounded-3xl max-w-2xl w-full max-h-[94vh] overflow-y-auto text-white shadow-2xl p-4 sm:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-start justify-between pb-3 sm:pb-4 border-b border-white/10 gap-2">
              <div>
                <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-[#E63946]">
                  SCHWEIZER HOSTING-BESTELLUNG
                </span>
                <h3 className="text-xl sm:text-2xl font-black text-white mt-0.5">
                  {selectedProduct.displayName}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowCheckoutModal(false)}
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-slate-300 transition shrink-0"
              >
                <X size={18} />
              </button>
            </div>

            {/* Stepper Indicator */}
            {checkoutStep < 5 && (
              <div className="flex items-center justify-between text-[11px] sm:text-xs font-bold text-slate-400 py-3 sm:py-4 border-b border-white/10 overflow-x-auto no-scrollbar gap-1 whitespace-nowrap">
                <span className={checkoutStep === 1 ? "text-[#E63946] font-black" : "text-slate-300"}>
                  1. Tarif
                </span>
                <ChevronRight size={13} className="text-slate-600 shrink-0" />
                <span className={checkoutStep === 2 ? "text-[#E63946] font-black" : checkoutStep > 2 ? "text-slate-300" : ""}>
                  2. Domain
                </span>
                <ChevronRight size={13} className="text-slate-600 shrink-0" />
                <span className={checkoutStep === 3 ? "text-[#E63946] font-black" : checkoutStep > 3 ? "text-slate-300" : ""}>
                  3. Kunde
                </span>
                <ChevronRight size={13} className="text-slate-600 shrink-0" />
                <span className={checkoutStep === 4 ? "text-[#E63946] font-black" : ""}>
                  4. Übersicht
                </span>
              </div>
            )}

            {/* Error Message with Interactive AJAX/jQuery Style Customer Auth Opener */}
            {orderError && (
              <div className="mt-4 p-4 rounded-2xl bg-gradient-to-r from-red-950/80 via-[#1f1118] to-amber-950/50 border-2 border-red-500/60 shadow-xl shadow-red-950/40 text-white animate-fadeIn">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-xl bg-red-500/20 text-red-400 border border-red-500/40 flex items-center justify-center shrink-0 mt-0.5 shadow-inner">
                      <LockKeyhole size={20} className="animate-pulse" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-black text-xs sm:text-sm text-red-300 uppercase tracking-wider">
                          {orderError.toLowerCase().includes("kundenbereich")
                            ? "Kundenbereich erforderlich"
                            : "Hinweis zur Bestellung"}
                        </span>
                        <span className="text-[10px] bg-red-500/20 text-red-300 px-2 py-0.5 rounded-full border border-red-500/30 font-bold">
                          Kunden-Login nötig
                        </span>
                      </div>
                      <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                        {orderError.toLowerCase().includes("kundenbereich")
                          ? "Für diese Aktion ist ein Kunden-Login erforderlich (oder Sie sind aktuell als Administrator angemeldet). Bitte melden Sie sich als Kunde an oder erstellen Sie ein neues Kundenkonto."
                          : orderError}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Interactive Clickable Trigger Button (AJAX Customer Auth Opener) */}
                <div className="mt-3.5 flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setShowCustomerAuthModal((prev) => !prev)}
                    className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#E63946] to-[#FF7A00] hover:from-[#c5303d] hover:to-[#e56d00] text-white font-black text-xs flex items-center justify-center gap-2 shadow-md cursor-pointer transition transform hover:scale-[1.02]"
                  >
                    <User size={14} />
                    <span>
                      {showCustomerAuthModal
                        ? "Anmeldebereich schliessen"
                        : "👉 Hier klicken: Kunden-Login & Registrierung öffnen"}
                    </span>
                    <ArrowRight size={13} />
                  </button>
                </div>

                {/* Smooth AJAX Slide-Down Customer Auth Area */}
                {showCustomerAuthModal && (
                  <div className="mt-4 pt-4 border-t border-white/10 animate-in fade-in slide-in-from-top-2 duration-200">
                    <CustomerAuthModal
                      isOpen={showCustomerAuthModal}
                      onClose={() => setShowCustomerAuthModal(false)}
                      onSuccess={(loggedUser) => {
                        setOrderError("");
                        setShowCustomerAuthModal(false);
                        if (setAuthUser) setAuthUser(loggedUser);
                        if (loggedUser) {
                          setCheckoutStep(4);
                        }
                      }}
                      title="Kundenkonto aktivieren"
                      subtitle="Melden Sie sich jetzt als Kunde an oder erstellen Sie in 30 Sekunden Ihr Kundenkonto:"
                    />
                  </div>
                )}
              </div>
            )}

            {/* STEP 1: TARIF & LAUFZEIT */}
            {checkoutStep === 1 && (
              <div className="py-5 space-y-5">
                <p className="text-sm text-slate-300">
                  Wählen Sie Ihre gewünschte Abrechnungsperiode:
                </p>

                {isAnnualOnlyProduct(selectedProduct) ? (
                  <div className="p-5 rounded-2xl border bg-[#E63946]/10 border-[#E63946] text-white">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <div className="font-extrabold text-base">Jährliche Registrierung & Verlängerung</div>
                        <div className="text-xs text-slate-400 mt-0.5">Offizielle DNS-Zone, Whois-Privacy & Schweizer Anycast</div>
                      </div>
                      <div className="font-mono text-2xl font-black text-emerald-400">
                        CHF {Number(selectedProduct.unitPrice).toFixed(2)}
                        <span className="text-xs font-normal text-slate-400"> / Jahr</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setOrderCycle("monthly")}
                      className={`p-4 rounded-2xl border text-left transition ${
                        orderCycle === "monthly"
                          ? "bg-[#E63946]/10 border-[#E63946] text-white"
                          : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
                      }`}
                    >
                      <div className="font-extrabold text-sm">Monatlich</div>
                      <div className="font-mono text-xl font-black mt-1">
                        CHF {Number(selectedProduct.unitPrice).toFixed(2)}
                        <span className="text-xs font-normal text-slate-400">/Mt.</span>
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1">Jederzeit kündbar</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setOrderCycle("yearly")}
                      className={`p-4 rounded-2xl border text-left transition relative ${
                        orderCycle === "yearly"
                          ? "bg-[#E63946]/10 border-[#E63946] text-white"
                          : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
                      }`}
                    >
                      <span className="absolute top-2 right-2 rounded-full bg-emerald-500/20 text-emerald-400 text-[9px] font-black px-2 py-0.5 border border-emerald-500/30">
                        10% RABATT
                      </span>
                      <div className="font-extrabold text-sm">Jährlich</div>
                      <div className="font-mono text-xl font-black mt-1 text-emerald-400">
                        CHF {(Number(selectedProduct.unitPrice) * 12 * 0.9).toFixed(2)}
                        <span className="text-xs font-normal text-slate-400">/Jahr</span>
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1">
                        ca. CHF {(Number(selectedProduct.unitPrice) * 0.9).toFixed(2)} / Mt.
                      </div>
                    </button>
                  </div>
                )}

                <div className="flex justify-end pt-4">
                  <button
                    type="button"
                    onClick={() => setCheckoutStep(2)}
                    className="px-6 py-2.5 rounded-xl bg-[#E63946] hover:bg-[#c5303d] text-white font-bold text-xs flex items-center gap-1.5"
                  >
                    <span>Weiter zur Domain</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: DOMAIN OPTIONEN */}
            {checkoutStep === 2 && (
              <div className="py-4 space-y-4">
                <p className="text-xs sm:text-sm text-slate-300">
                  Wie möchten Sie mit Ihrer Domain für dieses Hosting verfahren?
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <label
                    className={`flex items-start gap-3 p-3.5 rounded-2xl border cursor-pointer transition ${
                      domainChoice === "new"
                        ? "bg-[#E63946]/10 border-[#E63946] shadow-sm shadow-red-500/10"
                        : "bg-white/5 border-white/10 hover:bg-white/10"
                    }`}
                  >
                    <input
                      type="radio"
                      name="domainChoice"
                      value="new"
                      checked={domainChoice === "new"}
                      onChange={() => {
                        setDomainChoice("new");
                        setOrderError("");
                      }}
                      className="mt-1 accent-[#E63946]"
                    />
                    <div>
                      <div className="text-xs sm:text-sm font-bold text-white flex items-center gap-1.5">
                        <Sparkles size={13} className="text-[#E63946]" />
                        <span>Neue Domain registrieren</span>
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        Mit Live-Verfügbarkeitsprüfung (z.B. .ch ab CHF 15.-)
                      </div>
                    </div>
                  </label>

                  <label
                    className={`flex items-start gap-3 p-3.5 rounded-2xl border cursor-pointer transition ${
                      domainChoice === "transfer"
                        ? "bg-[#E63946]/10 border-[#E63946] shadow-sm shadow-red-500/10"
                        : "bg-white/5 border-white/10 hover:bg-white/10"
                    }`}
                  >
                    <input
                      type="radio"
                      name="domainChoice"
                      value="transfer"
                      checked={domainChoice === "transfer"}
                      onChange={() => {
                        setDomainChoice("transfer");
                        setOrderError("");
                      }}
                      className="mt-1 accent-[#E63946]"
                    />
                    <div>
                      <div className="text-xs sm:text-sm font-bold text-white flex items-center gap-1.5">
                        <ArrowRight size={13} className="text-blue-400" />
                        <span>Domain umziehen (Transfer)</span>
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        Kostenlos zu RedWORK per Auth-Code
                      </div>
                    </div>
                  </label>

                  <label
                    className={`flex items-start gap-3 p-3.5 rounded-2xl border cursor-pointer transition ${
                      domainChoice === "own"
                        ? "bg-[#E63946]/10 border-[#E63946] shadow-sm shadow-red-500/10"
                        : "bg-white/5 border-white/10 hover:bg-white/10"
                    }`}
                  >
                    <input
                      type="radio"
                      name="domainChoice"
                      value="own"
                      checked={domainChoice === "own"}
                      onChange={() => {
                        setDomainChoice("own");
                        setOrderError("");
                      }}
                      className="mt-1 accent-[#E63946]"
                    />
                    <div>
                      <div className="text-xs sm:text-sm font-bold text-white flex items-center gap-1.5">
                        <Globe size={13} className="text-emerald-400" />
                        <span>Eigene Domain (DNS-Record)</span>
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        Domain bleibt bei aktuellem Registrar
                      </div>
                    </div>
                  </label>

                  <label
                    className={`flex items-start gap-3 p-3.5 rounded-2xl border cursor-pointer transition ${
                      domainChoice === "later"
                        ? "bg-[#E63946]/10 border-[#E63946] shadow-sm shadow-red-500/10"
                        : "bg-white/5 border-white/10 hover:bg-white/10"
                    }`}
                  >
                    <input
                      type="radio"
                      name="domainChoice"
                      value="later"
                      checked={domainChoice === "later"}
                      onChange={() => {
                        setDomainChoice("later");
                        setOrderError("");
                      }}
                      className="mt-1 accent-[#E63946]"
                    />
                    <div>
                      <div className="text-xs sm:text-sm font-bold text-white flex items-center gap-1.5">
                        <Clock size={13} className="text-slate-400" />
                        <span>Später konfigurieren</span>
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        Domain nach Aufschaltung im Dashboard zuweisen
                      </div>
                    </div>
                  </label>
                </div>

                {/* Sub-section 1: NEUE DOMAIN MIT LIVE-CHECK */}
                {domainChoice === "new" && (
                  <div className="p-4 rounded-2xl bg-white/[0.04] border border-white/15 space-y-3 animate-fadeIn">
                    <label className="text-xs font-bold text-slate-200 block">
                      Wunschdomain auf Verfügbarkeit prüfen:
                    </label>

                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="relative flex-1">
                        <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          value={modalDomainQuery}
                          onChange={(e) => {
                            setModalDomainQuery(e.target.value);
                            setModalDomainResult(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleModalDomainCheck();
                            }
                          }}
                          placeholder="z.B. meine-firma"
                          className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white/5 border border-white/20 text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#E63946]"
                        />
                      </div>

                      <select
                        value={modalDomainTld}
                        onChange={(e) => {
                          setModalDomainTld(e.target.value);
                          setModalDomainResult(null);
                        }}
                        className="px-3 py-2.5 rounded-xl bg-[#0f172a] border border-white/20 text-xs sm:text-sm text-white font-mono font-bold focus:outline-none focus:border-[#E63946]"
                      >
                        <option value=".ch">.ch (CHF 15.00/J)</option>
                        <option value=".com">.com (CHF 12.00/J)</option>
                        <option value=".de">.de (CHF 8.00/J)</option>
                        <option value=".net">.net (CHF 16.00/J)</option>
                        <option value=".org">.org (CHF 18.00/J)</option>
                        <option value=".li">.li (CHF 19.00/J)</option>
                        <option value=".at">.at (CHF 18.00/J)</option>
                        <option value=".eu">.eu (CHF 14.00/J)</option>
                        <option value=".info">.info (CHF 19.00/J)</option>
                        <option value=".swiss">.swiss (CHF 120.00/J)</option>
                      </select>

                      <button
                        type="button"
                        onClick={() => handleModalDomainCheck()}
                        disabled={modalDomainChecking}
                        className="px-5 py-2.5 rounded-xl bg-[#E63946] hover:bg-[#c5303d] disabled:opacity-50 text-white font-bold text-xs flex items-center justify-center gap-1.5 shadow-md shadow-red-600/20 whitespace-nowrap"
                      >
                        {modalDomainChecking ? (
                          <>
                            <RefreshCw size={13} className="animate-spin" />
                            <span>Wird geprüft...</span>
                          </>
                        ) : (
                          <>
                            <Search size={13} />
                            <span>Verfügbarkeit prüfen</span>
                          </>
                        )}
                      </button>
                    </div>

                    {/* Result Card: Available */}
                    {modalDomainResult && modalDomainResult.available && (
                      <div className="p-3.5 rounded-xl bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs space-y-1.5 animate-fadeIn">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 size={16} className="text-emerald-400 flex-shrink-0" />
                            <span className="font-extrabold text-white text-sm">
                              {modalDomainResult.domain} ist frei!
                            </span>
                          </div>
                          <span className="font-mono font-black text-emerald-300 bg-emerald-950/60 px-2.5 py-1 rounded-md border border-emerald-500/30">
                            CHF {Number(modalDomainResult.price || 15).toFixed(2)} / Jahr
                          </span>
                        </div>
                        <p className="text-[11px] text-emerald-200/80">
                          ✓ Diese Domain wird mit Ihrem Hosting-Auftrag registriert und sofort aufgeschaltet.
                        </p>
                      </div>
                    )}

                    {/* Result Card: Taken */}
                    {modalDomainResult && !modalDomainResult.available && (
                      <div className="p-3.5 rounded-xl bg-amber-500/15 border border-amber-500/40 text-amber-200 text-xs space-y-2.5 animate-fadeIn">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-2">
                            <AlertCircle size={16} className="text-amber-400 flex-shrink-0" />
                            <span className="font-extrabold text-white text-sm">
                              {modalDomainResult.domain} ist vergeben
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setDomainChoice("transfer");
                              setDomainName(modalDomainResult.domain);
                              setModalDomainResult(null);
                            }}
                            className="px-3 py-1 rounded-lg bg-amber-400 hover:bg-amber-300 text-black font-extrabold text-[11px] flex items-center gap-1"
                          >
                            <span>Als Transfer übernehmen</span>
                            <ArrowRight size={12} />
                          </button>
                        </div>
                        <p className="text-[11px] text-amber-200/80">
                          Diese Domain ist bereits registriert. Falls Sie der Inhaber sind, können Sie sie einfach per Auth-Code zu RedWORK transferieren.
                        </p>

                        {/* TLD Suggestions */}
                        {modalDomainResult.suggestions && modalDomainResult.suggestions.length > 0 && (
                          <div className="pt-2 border-t border-amber-500/20">
                            <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1.5">
                              Freie Alternativen mit anderen Endungen:
                            </span>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                              {modalDomainResult.suggestions
                                .filter((s) => s.available)
                                .slice(0, 3)
                                .map((s) => (
                                  <button
                                    key={s.domain}
                                    type="button"
                                    onClick={() => {
                                      setModalDomainQuery(s.domain.split(".")[0]);
                                      setModalDomainTld(`.${s.tld.replace(/^\./, "")}`);
                                      setDomainName(s.domain);
                                      setModalDomainResult({
                                        domain: s.domain,
                                        available: true,
                                        price: s.price,
                                      });
                                    }}
                                    className="p-2 rounded-lg bg-black/40 hover:bg-emerald-950/40 border border-white/10 hover:border-emerald-500/40 text-left transition flex items-center justify-between"
                                  >
                                    <div>
                                      <span className="font-mono font-bold text-white text-xs block">
                                        {s.domain}
                                      </span>
                                      <span className="text-[10px] text-slate-400">
                                        CHF {Number(s.price).toFixed(2)}/J
                                      </span>
                                    </div>
                                    <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/20 px-1.5 py-0.5 rounded">
                                      Wählen
                                    </span>
                                  </button>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Sub-section 2: TRANSFER */}
                {domainChoice === "transfer" && (
                  <div className="p-4 rounded-2xl bg-white/[0.04] border border-white/15 space-y-3 animate-fadeIn">
                    <div>
                      <label className="text-xs font-bold text-slate-200 block mb-1">
                        Zu transferierende Domain:
                      </label>
                      <input
                        type="text"
                        value={domainName}
                        onChange={(e) => setDomainName(e.target.value.trim().toLowerCase())}
                        placeholder="meinedomain.ch"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-white/5 border border-white/20 text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#E63946]"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-bold text-slate-200 block mb-1">
                        Auth-Code / EPP-Schlüssel (optional, kann nachgereicht werden):
                      </label>
                      <input
                        type="text"
                        value={modalAuthCode}
                        onChange={(e) => setModalAuthCode(e.target.value)}
                        placeholder="z.B. XXXXX-XXXXX-XXXXX"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-white/5 border border-white/20 text-xs sm:text-sm text-white placeholder-slate-500 font-mono focus:outline-none focus:border-[#E63946]"
                      />
                    </div>
                    <p className="text-[11px] text-slate-400">
                      💡 Der Domainumzug ist für Sie kostenlos. Wir leiten den Transfer nach Bestellung direkt ein.
                    </p>
                  </div>
                )}

                {/* Sub-section 3: EIGENE DOMAIN */}
                {domainChoice === "own" && (
                  <div className="p-4 rounded-2xl bg-white/[0.04] border border-white/15 space-y-3 animate-fadeIn">
                    <div>
                      <label className="text-xs font-bold text-slate-200 block mb-1">
                        Bestehende Domain:
                      </label>
                      <input
                        type="text"
                        value={domainName}
                        onChange={(e) => setDomainName(e.target.value.trim().toLowerCase())}
                        placeholder="meinedomain.ch"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-white/5 border border-white/20 text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#E63946]"
                      />
                    </div>
                    <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-[11px] text-blue-200 leading-relaxed font-mono">
                      DNS-Hinweis: Hinterlegen Sie bei Ihrem Registrar den A-Record <span className="text-white font-bold">178.254.22.30</span> oder die Nameserver <span className="text-white font-bold">ns1.redwork.ch</span> / <span className="text-white font-bold">ns2.redwork.ch</span>.
                    </div>
                  </div>
                )}

                {/* Sub-section 4: SPÄTER KONFIGURIEREN */}
                {domainChoice === "later" && (
                  <div className="p-4 rounded-2xl bg-white/[0.04] border border-white/15 text-xs text-slate-300 leading-relaxed animate-fadeIn">
                    ✓ Sie können Ihr Hosting jetzt bestellen und die Domain später jederzeit mit einem Klick in Ihrem Kundendashboard verknüpfen oder bestellen.
                  </div>
                )}

                <div className="flex justify-between pt-4 border-t border-white/10">
                  <button
                    type="button"
                    onClick={() => setCheckoutStep(1)}
                    className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold text-xs"
                  >
                    Zurück
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (domainChoice === "new") {
                        if (!domainName.trim() || !modalDomainResult) {
                          setOrderError("Bitte prüfen Sie zuerst Ihre Wunschdomain mit 'Verfügbarkeit prüfen'.");
                          return;
                        }
                        if (!modalDomainResult.available) {
                          setOrderError("Die gewählte Domain ist vergeben. Bitte wählen Sie eine freie Domain oder den Transfer.");
                          return;
                        }
                      } else if (domainChoice === "transfer" || domainChoice === "own") {
                        if (!domainName.trim()) {
                          setOrderError("Bitte Domainname eingeben oder 'Später konfigurieren' wählen.");
                          return;
                        }
                      }
                      setOrderError("");
                      if (user && (user.role === "customer" || !user.role)) {
                        setCheckoutStep(4);
                      } else {
                        setCheckoutStep(3);
                      }
                    }}
                    className="px-6 py-2.5 rounded-xl bg-[#E63946] hover:bg-[#c5303d] text-white font-bold text-xs flex items-center gap-1.5"
                  >
                    <span>Weiter</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3: KUNDENANGABEN / LOGIN / REGISTER (AJAX Powered) */}
            {checkoutStep === 3 && (
              <div className="py-4 space-y-4">
                <CustomerAuthModal
                  isOpen={true}
                  onSuccess={(loggedUser) => {
                    setOrderError("");
                    if (setAuthUser) setAuthUser(loggedUser);
                    setCheckoutStep(4);
                  }}
                  title="3. Schritt: Kundenkonto für Ihre Bestellung"
                  subtitle="Melden Sie sich an oder erstellen Sie in wenigen Schritten Ihr Schweizer Kundenkonto:"
                />
                <div className="flex justify-between pt-3 border-t border-white/10">
                  <button
                    type="button"
                    onClick={() => setCheckoutStep(2)}
                    className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold text-xs cursor-pointer"
                  >
                    Zurück zur Domain
                  </button>
                </div>
              </div>
            )}

            {/* STEP 4: ÜBERSICHT & ZAHLUNGSMETHODE */}
            {checkoutStep === 4 && (
              <div className="py-4 space-y-4">
                {/* Vertrags- & Bestellnummer Header Card */}
                <div className="bg-gradient-to-r from-red-950/40 via-[#1a111a] to-[#0f172a] border border-[#E63946]/40 rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3 shadow-md shadow-red-900/10">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-[#E63946] block">
                      Ihre verbindliche Vertrags- & Bestellnummer:
                    </span>
                    <span className="font-mono text-lg sm:text-xl font-black text-white tracking-widest">
                      {orderRef || "RW-2026-CH"}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (orderRef) {
                        navigator.clipboard?.writeText(orderRef);
                        setOrderRefCopied(true);
                        setTimeout(() => setOrderRefCopied(false), 2500);
                      }
                    }}
                    className="px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-bold text-white flex items-center gap-1.5 transition"
                  >
                    {orderRefCopied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                    <span>{orderRefCopied ? "Kopiert!" : "Nummer kopieren"}</span>
                  </button>
                </div>

                <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 sm:p-5 space-y-3">
                  <div className="flex justify-between text-xs sm:text-sm pb-2 border-b border-white/10">
                    <span className="text-slate-400">Produkt:</span>
                    <span className="font-bold text-white">{selectedProduct.displayName}</span>
                  </div>

                  <div className="flex justify-between text-xs sm:text-sm pb-2 border-b border-white/10">
                    <span className="text-slate-400">Abrechnung:</span>
                    <span className="font-bold text-white capitalize">
                      {isAnnualOnlyProduct(selectedProduct)
                        ? "Jährlich"
                        : orderCycle === "yearly"
                        ? "Jährlich (10% Rabatt)"
                        : "Monatlich"}
                    </span>
                  </div>

                  <div className="flex justify-between text-xs sm:text-sm pb-2 border-b border-white/10">
                    <span className="text-slate-400">Domain:</span>
                    <span className="font-mono font-bold text-[#D4AF37]">
                      {domainChoice === "later" ? "Wird später konfiguriert" : domainName || "Eigene Domain"}
                    </span>
                  </div>

                  <div className="flex justify-between text-xs sm:text-sm pb-2 border-b border-white/10">
                    <span className="text-slate-400">Serverstandort:</span>
                    <span className="font-bold text-emerald-400">🇨🇭 Schweiz (Zürich)</span>
                  </div>

                  {/* Price Sum */}
                  <div className="pt-2 flex justify-between items-baseline flex-wrap gap-2">
                    <span className="text-xs sm:text-sm font-extrabold text-white">Gesamtbetrag (inkl. 8.1% MwSt):</span>
                    <span className="text-xl sm:text-2xl font-black font-mono text-[#E63946]">
                      CHF {getOrderTotalAmount(selectedProduct, orderCycle)}
                    </span>
                  </div>
                </div>

                {/* Swiss Payment Methods */}
                <div className="space-y-3">
                  <label className="text-xs font-bold text-slate-300 block">
                    Zahlungsmethode auswählen:
                  </label>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod("twint")}
                      className={`p-3 rounded-xl border flex flex-col items-center justify-center gap-1.5 transition ${
                        paymentMethod === "twint"
                          ? "bg-[#D4AF37]/15 border-[#D4AF37] text-white shadow-[0_0_15px_rgba(212,175,55,0.25)]"
                          : "bg-white/5 border-white/10 text-slate-400 hover:bg-white/10"
                      }`}
                    >
                      <Zap size={20} className="text-[#D4AF37]" />
                      <span className="font-black text-xs">TWINT</span>
                      <span className="text-[10px] text-amber-300 font-bold hidden sm:inline">Sofort</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setPaymentMethod("invoice")}
                      className={`p-3 rounded-xl border flex flex-col items-center justify-center gap-1.5 transition ${
                        paymentMethod === "invoice"
                          ? "bg-[#E63946]/15 border-[#E63946] text-white shadow-[0_0_15px_rgba(230,57,70,0.25)]"
                          : "bg-white/5 border-white/10 text-slate-400 hover:bg-white/10"
                      }`}
                    >
                      <CreditCard size={20} className="text-[#E63946]" />
                      <span className="font-black text-xs">QR-Rechnung</span>
                      <span className="text-[10px] text-slate-400 hidden sm:inline">Per E-Mail</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setPaymentMethod("card")}
                      className={`p-3 rounded-xl border flex flex-col items-center justify-center gap-1.5 transition ${
                        paymentMethod === "card"
                          ? "bg-emerald-500/15 border-emerald-500 text-white shadow-[0_0_15px_rgba(16,185,129,0.25)]"
                          : "bg-white/5 border-white/10 text-slate-400 hover:bg-white/10"
                      }`}
                    >
                      <Lock size={20} className="text-emerald-400" />
                      <span className="font-black text-xs">Kreditkarte</span>
                      <span className="text-[10px] text-slate-400 hidden sm:inline">Visa / MC</span>
                    </button>
                  </div>

                  {/* DEDICATED TWINT INSTRUCTION BOX */}
                  {paymentMethod === "twint" && (
                    <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-b from-[#1c1807] via-[#120f04] to-[#0a0802] border-2 border-[#D4AF37] shadow-[0_0_30px_rgba(212,175,55,0.2)] space-y-4 animate-fadeIn">
                      <div className="flex items-center justify-between pb-3 border-b border-[#D4AF37]/30 flex-wrap gap-2">
                        <div className="flex items-center gap-2.5">
                          <div className="h-9 w-9 rounded-xl bg-[#D4AF37]/20 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] font-black text-sm">
                            📱
                          </div>
                          <div>
                            <span className="text-[10px] font-mono font-black uppercase text-[#D4AF37] tracking-wider block">
                              Offizielle TWINT Direktüberweisung Schweiz
                            </span>
                            <h4 className="text-xs sm:text-sm font-black text-white">
                              Manuelle TWINT Überweisung
                            </h4>
                          </div>
                        </div>
                        <span className="px-2.5 py-1 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37] text-[#D4AF37] font-bold text-[10px]">
                          SCHNELLSTE AUFSCHALTUNG
                        </span>
                      </div>

                      {/* Recipient Number Box with 1-Click Copy */}
                      <div className="bg-black/60 rounded-xl p-3.5 sm:p-4 border border-[#D4AF37]/40 flex items-center justify-between flex-wrap gap-3">
                        <div>
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                            TWINT Empfänger-Telefonnummer:
                          </span>
                          <span className="font-mono text-xl sm:text-2xl font-black text-white tracking-widest">
                            076 610 61 08
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard?.writeText("0766106108");
                            setTwintCopied(true);
                            setTimeout(() => setTwintCopied(false), 2500);
                          }}
                          className="px-4 py-2 rounded-xl bg-[#D4AF37] hover:bg-[#b8952b] text-black font-black text-xs flex items-center gap-1.5 transition shadow-md"
                        >
                          {twintCopied ? <Check size={14} /> : <Copy size={14} />}
                          <span>{twintCopied ? "Kopiert!" : "076 610 61 08 kopieren"}</span>
                        </button>
                      </div>

                      {/* Critical TWINT Instructions */}
                      <div className="p-3.5 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-xs text-amber-200/90 leading-relaxed space-y-2">
                        <div className="font-black text-white flex items-center gap-1.5 text-xs sm:text-sm">
                          <AlertCircle size={15} className="text-[#D4AF37] flex-shrink-0" />
                          <span>WICHTIGE ANWEISUNG FÜR IHRE TWINT-ZAHLUNG:</span>
                        </div>
                        <p>
                          Bitte überweisen Sie den fälligen Betrag von{" "}
                          <strong className="text-white font-mono font-black text-sm">
                            CHF {getOrderTotalAmount(selectedProduct, orderCycle)}
                          </strong>{" "}
                          manuell per TWINT an die Nummer <strong className="text-white font-mono">076 610 61 08</strong>.
                        </p>
                        <div className="p-3 rounded-lg bg-black/60 border border-[#D4AF37]/40 text-slate-200 space-y-1">
                          <div className="flex items-center justify-between flex-wrap gap-1">
                            <span className="text-[11px] font-bold text-slate-400">Zahlungszweck / Notiz im TWINT:</span>
                            <span className="font-mono font-black text-[#D4AF37] text-sm tracking-widest">{orderRef}</span>
                          </div>
                          <p className="text-[11px] text-amber-200/90">
                            👉 Geben Sie unbedingt Ihre Vertragsnummer <strong className="text-white font-mono">{orderRef}</strong> im Zahlungszweck Ihrer TWINT-App an, damit wir die Zahlung sofort automatisch Ihrem neuen Account zuordnen können!
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-between pt-4 border-t border-white/10">
                  <button
                    type="button"
                    onClick={() => setCheckoutStep(user ? 2 : 3)}
                    className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold text-xs"
                  >
                    Zurück
                  </button>
                  <button
                    type="button"
                    disabled={orderSubmitting}
                    onClick={handleFinalOrderSubmit}
                    className="px-6 sm:px-8 py-3 rounded-xl bg-[#E63946] hover:bg-[#c5303d] disabled:opacity-50 text-white font-black text-xs sm:text-sm flex items-center gap-2 shadow-lg shadow-red-600/30"
                  >
                    {orderSubmitting ? (
                      <>
                        <RefreshCw size={15} className="animate-spin" />
                        <span>Bestellung wird ausgeführt...</span>
                      </>
                    ) : (
                      <>
                        <CheckCheck size={16} />
                        <span>Zahlungspflichtig bestellen</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* STEP 5: ERFOLG */}
            {checkoutStep === 5 && (
              <div className="py-6 sm:py-8 text-center space-y-4">
                <div className="h-16 w-16 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 flex items-center justify-center mx-auto">
                  <CheckCircle2 size={36} />
                </div>
                <h3 className="text-xl sm:text-2xl font-black text-white">
                  Vielen Dank für Ihre Bestellung!
                </h3>
                <p className="text-xs sm:text-sm text-slate-300 max-w-md mx-auto leading-relaxed">
                  Ihr Auftrag wurde erfolgreich entgegengenommen. Sie erhalten in Kürze Ihre Auftragsbestätigung und die Zugangsdaten per E-Mail.
                </p>

                {/* Contract / Order ID Card */}
                <div className="p-4 rounded-2xl bg-white/5 border border-white/10 max-w-sm mx-auto text-xs text-left space-y-1 font-mono">
                  <div className="text-slate-400">Vertrags- & Bestellnummer:</div>
                  <div className="text-[#D4AF37] font-black text-sm tracking-wider">
                    {orderSuccessData?.reference || orderSuccessData?.contractNumber || orderRef || "#RW-ORDER"}
                  </div>
                </div>

                {/* TWINT Reminder for Step 5 */}
                {paymentMethod === "twint" && (
                  <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-b from-[#1c1807] via-[#120f04] to-[#0a0802] border-2 border-[#D4AF37] text-left text-xs space-y-3 max-w-md mx-auto shadow-lg shadow-amber-950/30 animate-fadeIn">
                    <div className="flex items-center gap-2 text-[#D4AF37] font-black uppercase tracking-wider text-xs">
                      <span>📱 Ausstehende TWINT-Zahlung</span>
                    </div>
                    <p className="text-slate-200 leading-relaxed text-xs">
                      Bitte senden Sie den fälligen Betrag jetzt über Ihre TWINT-App:
                    </p>
                    <div className="bg-black/60 p-3 rounded-xl border border-[#D4AF37]/30 font-mono text-xs space-y-1.5">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400">TWINT Nummer:</span>
                        <span className="text-white font-black text-sm">076 610 61 08</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400">Vertragsnummer:</span>
                        <span className="text-[#D4AF37] font-black">{orderRef}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400">Betrag:</span>
                        <span className="text-white font-black">
                          CHF {getOrderTotalAmount(selectedProduct, orderCycle)}
                        </span>
                      </div>
                    </div>
                    <p className="text-[11px] text-amber-200/80">
                      Bitte schreiben Sie <strong className="text-white">{orderRef}</strong> in das Bemerkungsfeld bei TWINT. Ihr Hosting wird direkt nach Eingang aktiviert.
                    </p>
                  </div>
                )}

                <div className="pt-4 flex items-center justify-center gap-3 flex-wrap">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCheckoutModal(false);
                      navigate("/dashboard");
                    }}
                    className="px-6 py-2.5 rounded-xl bg-[#E63946] text-white font-bold text-xs"
                  >
                    Zum Kundendashboard
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCheckoutModal(false)}
                    className="px-5 py-2.5 rounded-xl bg-white/10 text-white font-bold text-xs"
                  >
                    Schliessen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
